// ══════════════════════════════════════════════════════════════════════════════
// PujaPage.js — Online Puja Booking · VedSanga
// Individual or Family · 45-min sessions · Live video with priest
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";
import PujaRoom from "./PujaRoom";
import PriestPortalTab from "./PriestPortalTab";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// ── Puja catalogue ─────────────────────────────────────────────────────────────
const PUJAS = [
  {
    id: "shiva-abhishekam",
    name: "Shiva Abhishekam",
    deity: "Lord Shiva",
    emoji: "🔱",
    category: "simple",
    tags: ["Monday"],
    benefit: "Peace, health, removal of obstacles",
    sanskrit: "ॐ नमः शिवाय",
    color: "#6366f1",
    home_ok: true,
    home_setup: null,
    popular: false,
  },
  {
    id: "hanuman-puja",
    name: "Hanuman Puja",
    deity: "Lord Hanuman",
    emoji: "🙏",
    category: "simple",
    tags: ["Tuesday", "Saturday"],
    benefit: "Strength, courage, protection from evil",
    sanskrit: "जय हनुमान ज्ञान गुण सागर",
    color: "#f97316",
    home_ok: true,
    home_setup: null,
    popular: true,
  },
  {
    id: "lakshmi-puja",
    name: "Lakshmi Puja",
    deity: "Goddess Lakshmi",
    emoji: "🌸",
    category: "simple",
    tags: ["Friday"],
    benefit: "Wealth, abundance, harmony at home",
    sanskrit: "ॐ श्री महालक्ष्म्यै नमः",
    color: "#ec4899",
    home_ok: true,
    home_setup: null,
    popular: false,
  },
  {
    id: "vishnu-sahasranama",
    name: "Vishnu Sahasranama Puja",
    deity: "Lord Vishnu",
    emoji: "🪷",
    category: "simple",
    tags: ["Thursday"],
    benefit: "Prosperity, family wellbeing, moksha",
    sanskrit: "ॐ नमो भगवते वासुदेवाय",
    color: "#14b8a6",
    home_ok: true,
    home_setup: null,
    popular: false,
  },
  {
    id: "shani-puja",
    name: "Shani Puja",
    deity: "Lord Shani",
    emoji: "⚫",
    category: "simple",
    tags: ["Saturday"],
    benefit: "Relief from Shani dosha, karma clearing",
    sanskrit: "ॐ शं शनिश्चराय नमः",
    color: "#64748b",
    home_ok: true,
    home_setup: null,
    popular: true,
  },
  {
    id: "satyanarayana",
    name: "Satyanarayana Puja",
    deity: "Lord Vishnu (Satyanarayana)",
    emoji: "🪔",
    category: "vrata",
    tags: ["Purnima", "Any auspicious day"],
    benefit: "Family blessings, success in new ventures",
    sanskrit: "ॐ नमो भगवते वासुदेवाय",
    color: "#f59e0b",
    home_ok: true,
    home_setup: null,
    popular: true,
  },
  {
    id: "ganesh-puja",
    name: "Ganesh Puja",
    deity: "Lord Ganesha",
    emoji: "🐘",
    category: "vrata",
    tags: ["Chaturthi", "New beginnings"],
    benefit: "Remove obstacles, bless new beginnings",
    sanskrit: "ॐ गं गणपतये नमः",
    color: "#f97316",
    home_ok: true,
    home_setup: null,
    popular: true,
  },
  {
    id: "durga-puja",
    name: "Durga Puja",
    deity: "Goddess Durga",
    emoji: "🔴",
    category: "vrata",
    tags: ["Navratri", "Fridays"],
    benefit: "Shakti, protection, victory over adversity",
    sanskrit: "ॐ दुं दुर्गायै नमः",
    color: "#ef4444",
    home_ok: true,
    home_setup: null,
    popular: false,
  },
  {
    id: "grihapravesh",
    name: "Grihapravesh Puja",
    deity: "Vastu Devata + Ganesha",
    emoji: "🏠",
    category: "occasion",
    tags: ["Housewarming"],
    benefit: "Bless new home, positive vastu energy",
    sanskrit: "ॐ वास्तोष्पते प्रतिजानीह्यस्मान्",
    color: "#22c55e",
    home_ok: false,
    home_setup: {
      note: "Priest performs at a sacred space and streams live. You participate from your new home.",
      items: ["Lamp (diya) with ghee or oil", "Fresh flowers (marigold preferred)", "A photo or drawing of your new home", "A small plate of raw rice", "Kumkum & turmeric", "Agarbatti (incense sticks)"],
      actions: ["Light your lamp when the priest lights his", "Place your hand on the photo of the home during the main mantra", "Sprinkle a few drops of the water the priest blesses — we will guide you live"],
    },
    popular: false,
  },
  {
    id: "navagraha",
    name: "Navagraha Shanti Puja",
    deity: "Nine Planets",
    emoji: "🪐",
    category: "graha",
    tags: ["Graha dosha", "Before major events"],
    benefit: "Balance all 9 planetary energies",
    sanskrit: "ॐ नवग्रहाय नमः",
    color: "#8b5cf6",
    home_ok: false,
    home_setup: {
      note: "Full Navagraha havan requires a sacred fire. Priest performs at a dedicated space and streams live. You participate actively from home.",
      items: ["9 small lamps or 1 diya", "9 flowers (mix of colours if possible)", "A small havan kund or metal bowl (for symbolic fire at home)", "Camphor (for home flame lighting)", "Til (black sesame seeds)", "Akshat (raw rice with turmeric)", "9 types of grains if available (wheat, rice, dal, etc.)"],
      actions: ["Light your lamp at the same time as the priest lights the havan", "Hold each flower as the priest calls each planet's name", "Offer sesame seeds into your bowl/flame for Shani, rice for Chandra, wheat for Surya", "Chant along with the priest — lyrics will appear on screen during the puja"],
    },
    popular: true,
  },
  {
    id: "kuja-dosha",
    name: "Kuja Dosha Nivaran",
    deity: "Lord Mangala (Mars)",
    emoji: "🔴",
    category: "graha",
    tags: ["Before marriage", "Kuja dosha"],
    benefit: "Reduce Manglik dosha, marital harmony",
    sanskrit: "ॐ अं अङ्गारकाय नमः",
    color: "#ef4444",
    home_ok: true,
    home_setup: null,
    popular: false,
  },
  {
    id: "rahu-ketu",
    name: "Rahu-Ketu Shanti",
    deity: "Rahu & Ketu",
    emoji: "🐍",
    category: "graha",
    tags: ["Rahu/Ketu dasha"],
    benefit: "Clarity, remove confusion, spiritual protection",
    sanskrit: "ॐ रां राहवे नमः",
    color: "#a855f7",
    home_ok: false,
    home_setup: {
      note: "Rahu-Ketu Shanti involves specific rituals. Priest performs at a sacred space and streams live. You perform a simplified parallel ritual at home.",
      items: ["Black cloth or dark-coloured plate", "Black sesame seeds (til)", "Dark blue or black flowers if available, else any flowers", "A lamp with sesame oil (or coconut oil)", "Dakshina plate (a coin or small offering)", "Agarbatti"],
      actions: ["Light your lamp when the priest begins", "Place black sesame seeds on the dark plate", "Offer sesame seeds three times when the priest says 'Rahu namah'", "Keep the dakshina plate near you throughout — offer it symbolically at the end"],
    },
    popular: false,
  },
];

// ── Indian States for personalisation ─────────────────────────────────────────
const INDIA_STATES = [
  { value: "uttar_pradesh",    label: "Uttar Pradesh",   lang: "hindi"   },
  { value: "bihar",            label: "Bihar",           lang: "hindi"   },
  { value: "madhya_pradesh",   label: "Madhya Pradesh",  lang: "hindi"   },
  { value: "rajasthan",        label: "Rajasthan",       lang: "hindi"   },
  { value: "delhi",            label: "Delhi",           lang: "hindi"   },
  { value: "haryana",          label: "Haryana",         lang: "hindi"   },
  { value: "uttarakhand",      label: "Uttarakhand",     lang: "hindi"   },
  { value: "jharkhand",        label: "Jharkhand",       lang: "hindi"   },
  { value: "chhattisgarh",     label: "Chhattisgarh",   lang: "hindi"   },
  { value: "andhra_pradesh",   label: "Andhra Pradesh",  lang: "telugu"  },
  { value: "telangana",        label: "Telangana",       lang: "telugu"  },
  { value: "tamil_nadu",       label: "Tamil Nadu",      lang: "tamil"   },
  { value: "west_bengal",      label: "West Bengal",     lang: "bengali" },
  { value: "assam",            label: "Assam",           lang: "bengali" },
  { value: "maharashtra",      label: "Maharashtra",     lang: "marathi" },
  { value: "karnataka",        label: "Karnataka",       lang: "kannada" },
  { value: "gujarat",          label: "Gujarat",         lang: "gujarati"},
  { value: "kerala",           label: "Kerala",          lang: "malayalam"},
  { value: "punjab",           label: "Punjab",          lang: "punjabi" },
  { value: "himachal_pradesh", label: "Himachal Pradesh",lang: "punjabi" },
  { value: "odisha",           label: "Odisha",          lang: "odia"    },
  { value: "goa",              label: "Goa",             lang: "marathi" },
  { value: "other",            label: "Other / NRI",     lang: "hindi"   },
];

const TRADITIONS = [
  { value: "all",       label: "No preference" },
  { value: "shaiva",    label: "Shaiva (Shiva devotee)" },
  { value: "vaishnava", label: "Vaishnava (Vishnu devotee)" },
  { value: "shakta",    label: "Shakta (Devi devotee)" },
];

// Language display names for UI
const LANG_LABELS = {
  hindi: "Hindi", telugu: "Telugu", tamil: "Tamil",
  bengali: "Bengali", marathi: "Marathi", kannada: "Kannada",
  gujarati: "Gujarati", malayalam: "Malayalam", punjabi: "Punjabi",
  odia: "Odia",
};

const CATEGORIES = [
  { id: "all",      label: "All Pujas",      icon: "🪔" },
  { id: "simple",   label: "Daily Pujas",    icon: "📅" },
  { id: "vrata",    label: "Vratas",         icon: "🎉" },
  { id: "graha",    label: "Astrology",      icon: "🪐" },
  { id: "occasion", label: "Life Events",    icon: "🏠" },
  { id: "homa",     label: "Homa / Yagna",   icon: "🔥" },
];

// ── Guest landing constants ────────────────────────────────────────────────────
const SACRED_ANIM_CSS = `
  @keyframes flameFlicker {
    0%,100% { transform: scaleY(1) scaleX(1); opacity: 0.95; }
    25%  { transform: scaleY(1.12) scaleX(0.9);  opacity: 1;    }
    50%  { transform: scaleY(0.93) scaleX(1.06); opacity: 0.88; }
    75%  { transform: scaleY(1.1)  scaleX(0.91); opacity: 1;    }
  }
  @keyframes lampSway {
    0%,100% { transform: rotate(-3deg); transform-origin: bottom center; }
    50%      { transform: rotate(3deg);  transform-origin: bottom center; }
  }
  @keyframes flamePulse {
    0%,100% { filter: drop-shadow(0 0 8px rgba(255,140,0,0.6)) drop-shadow(0 0 20px rgba(255,100,0,0.3)); }
    50%      { filter: drop-shadow(0 0 18px rgba(255,160,0,0.95)) drop-shadow(0 0 45px rgba(255,80,0,0.55)); }
  }
  @keyframes sacredFadeIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes guestNeedPop {
    from { opacity: 0; transform: scale(0.95); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes goldenPulse {
    0%,100% { opacity: 0.55; transform: scale(1);    }
    50%      { opacity: 0.95; transform: scale(1.03); }
  }
  @keyframes petalFall {
    0%   { transform: translateY(-10px) rotate(0deg);   opacity: 0;    }
    8%   { opacity: 0.75; }
    88%  { opacity: 0.5; }
    100% { transform: translateY(140px) rotate(220deg); opacity: 0;    }
  }
  @keyframes smokeRise {
    0%   { transform: translateY(0)    translateX(0)   scaleX(1);   opacity: 0.35; }
    45%  { transform: translateY(-28px) translateX(4px) scaleX(1.6); opacity: 0.15; }
    100% { transform: translateY(-60px) translateX(-3px) scaleX(2.3); opacity: 0;   }
  }
  @keyframes brassGlow {
    0%,100% { box-shadow: 0 0 14px rgba(201,149,58,0.25), inset 0 1px 0 rgba(255,220,100,0.15); }
    50%      { box-shadow: 0 0 26px rgba(201,149,58,0.5),  inset 0 1px 0 rgba(255,220,100,0.3);  }
  }
  @keyframes lampHeroGlow {
    0%,100% { opacity: 0.5; }
    50%      { opacity: 0.8; }
  }
  @keyframes mandalaRotateSlow {
    from { transform: translate(-50%,-50%) rotate(0deg);   }
    to   { transform: translate(-50%,-50%) rotate(360deg); }
  }
  @keyframes stoneShimmer {
    0%,100% { opacity: 0.03; }
    50%      { opacity: 0.07; }
  }
  @keyframes bell-ring {
    0%{transform:rotate(0deg)} 8%{transform:rotate(18deg)} 16%{transform:rotate(-16deg)}
    24%{transform:rotate(12deg)} 32%{transform:rotate(-10deg)} 40%{transform:rotate(6deg)}
    48%{transform:rotate(-4deg)} 56%{transform:rotate(2deg)} 64%{transform:rotate(-1deg)}
    100%{transform:rotate(0deg)}
  }
  @keyframes sacred-appear {
    from{opacity:0;transform:scale(0.92) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)}
  }
  @keyframes glow-pulse {
    0%,100%{opacity:0.7} 50%{opacity:1}
  }
`;

const DAY_PUJA = {
  0: { id: "shiva-abhishekam", emoji: "☀️", name: "Surya Namaskar Puja", day: "Ravivar",   mantra: "ॐ सूर्याय नमः",               god: "Surya Dev"   },
  1: { id: "shiva-abhishekam", emoji: "🔱", name: "Shiva Abhishekam",    day: "Somvar",    mantra: "ॐ नमः शिवाय",                 god: "Shiv Ji"     },
  2: { id: "hanuman-puja",     emoji: "🙏", name: "Hanuman Puja",        day: "Mangalvar", mantra: "जय बजरंगबली",                 god: "Hanuman Ji"  },
  3: { id: "ganesh-puja",      emoji: "🐘", name: "Ganesh Puja",         day: "Budhvar",   mantra: "ॐ गं गणपतये नमः",             god: "Ganesh Ji"   },
  4: { id: "vishnu-sahasranama",emoji:"🪷", name: "Vishnu Sahasranama",  day: "Guruvar",   mantra: "ॐ नमो भगवते वासुदेवाय",      god: "Vishnu Ji"   },
  5: { id: "lakshmi-puja",     emoji: "🌸", name: "Lakshmi Puja",        day: "Shukravar", mantra: "ॐ श्री महालक्ष्म्यै नमः",    god: "Lakshmi Ma"  },
  6: { id: "shani-puja",       emoji: "⚫", name: "Shani Puja",          day: "Shanivar",  mantra: "ॐ शं शनिश्चराय नमः",         god: "Shani Dev"   },
};

const NEEDS = [
  { id: "peace",      icon: "🕊️", label: "Peace & Clarity",      hindi: "शांति",   pujas: ["shiva-abhishekam", "vishnu-sahasranama"] },
  { id: "protection", icon: "🛡️", label: "Protection",           hindi: "रक्षा",   pujas: ["hanuman-puja", "durga-puja"]             },
  { id: "wealth",     icon: "🪙", label: "Wealth & Abundance",   hindi: "समृद्धि", pujas: ["lakshmi-puja", "satyanarayana"]          },
  { id: "health",     icon: "💚", label: "Health & Healing",     hindi: "आरोग्य",  pujas: ["shiva-abhishekam", "navagraha"]          },
  { id: "family",     icon: "🏠", label: "Family Harmony",       hindi: "परिवार",  pujas: ["satyanarayana", "ganesh-puja"]           },
  { id: "marriage",   icon: "💑", label: "Marriage & Love",      hindi: "विवाह",   pujas: ["kuja-dosha", "satyanarayana"]            },
];

// ── Pricing table (mirrors puja_catalog.py — all prices end in 1) ─────────────
const PRICING = {
  simple:           { satsang: 151,  mandali: 301,  yugal:  751, ekanta: 1751, ekanta_family: 2001, ekanta_couple: 2251 },
  vrata:            { satsang: 251,  mandali: 401,  yugal: 1001, ekanta: 2251, ekanta_family: 2501, ekanta_couple: 2751 },
  graha:            { satsang: 351,  mandali: 551,  yugal: 1251, ekanta: 2751, ekanta_family: 3001, ekanta_couple: 3251 },
  homa:             { satsang: 551,  mandali: 751,  yugal: 1751, ekanta: 3751, ekanta_family: 4251, ekanta_couple: 4751 },
  abhishek:         { satsang: 351,  mandali: 551,  yugal: 1251, ekanta: 2751, ekanta_family: 3001, ekanta_couple: 3251 },
  occasion_simple:  { ekanta: 1751, ekanta_family: 2001, ekanta_couple: 2251 },
  occasion_medium:  { ekanta: 2251, ekanta_family: 2751, ekanta_couple: 3251 },
  occasion_complex: { ekanta: 3751, ekanta_family: 4251, ekanta_couple: 5251 },
  occasion_vivah:   { ekanta: 5251, ekanta_family: 6251, ekanta_couple: 7251 },
};

// Samagri contribution (per family, goes 100% to priest — shown for community tiers only)
// Fewer families sharing = higher per-family contribution to cover real priest cost.
// Ekanta: samagri included in puja price — never shown separately.
const SAMAGRI = {
  //              satsang(10)  mandali(5)  yugal(2)
  simple:   { satsang:  51, mandali:  81, yugal: 151 },
  vrata:    { satsang: 101, mandali: 151, yugal: 201 },
  abhishek: { satsang: 101, mandali: 151, yugal: 201 },
  graha:    { satsang: 151, mandali: 201, yugal: 251 },
  homa:     { satsang: 301, mandali: 351, yugal: 401 },
};

function getSamagriCost(puja, tierKey) {
  if (!tierKey || tierKey.startsWith("ekanta")) return 0;
  const pt = puja?.pricing_tier || "simple";
  return SAMAGRI[pt]?.[tierKey] ?? 51;
}

// Helper: get price for a puja at a specific tier (bookingType affects ekanta pricing)
function getPujaPrice(puja, tierKey, bookingType) {
  const pt = puja?.pricing_tier || "simple";
  const prices = PRICING[pt] || {};
  // For ekanta, price depends on who is booking
  if (tierKey === "ekanta") {
    if (bookingType === "couple")  return prices.ekanta_couple ?? prices.ekanta ?? null;
    if (bookingType === "family")  return prices.ekanta_family ?? prices.ekanta ?? null;
    return prices.ekanta ?? null;
  }
  return prices[tierKey] ?? null;
}

// Helper: which tiers are available for this puja?
function getAvailableTiers(puja) {
  const allowed = puja?.session_types || ["satsang","mandali","yugal","ekanta"];
  return TIERS_BASE.filter(t => allowed.includes(t.key));
}

// ── Session tiers (structure only — prices come from puja) ────────────────────
const TIERS_BASE = [
  {
    key:              "satsang",
    label:            "Satsang",
    slots:            10,
    slot_label:       "10 devotees per session",
    family_slot_label:"10 families per session",
    tagline:          "Pray together, pay less — strength of a sacred circle",
    badge:      null,
    color:      "#14b8a6",
    experience: "You join a live group puja with up to 9 other devotees. The priest reads every name and sankalpa at the start. Chant along — lyrics shown on screen throughout.",
    includes: [
      "Live video puja (duration based on puja type)",
      "Your name & sankalpa read aloud",
      "Chant-along lyrics on screen",
      "Prasad photo sent after puja",
      "Recording access for 24 hours",
    ],
  },
  {
    key:              "mandali",
    label:            "Mandali",
    slots:            5,
    slot_label:       "5 devotees per session",
    family_slot_label:"5 families per session",
    tagline:          "A closer circle — the priest knows your name, your story",
    badge:      "Most Chosen",
    color:      "#f59e0b",
    experience: "A smaller group of 5 devotees. The priest dedicates more time to each sankalpa, mentions your name and gotra in the ritual, and addresses you directly.",
    includes: [
      "Live video puja",
      "Detailed sankalpa reading",
      "Name & gotra mentioned in ritual",
      "Chant-along lyrics on screen",
      "Prasad photo + blessings audio clip",
      "Recording access for 7 days",
    ],
  },
  {
    key:              "yugal",
    label:            "Yugal",
    slots:            2,
    slot_label:       "2 devotees per session",
    family_slot_label:"2 families per session",
    tagline:          "Near-private — priest sees only you and one other family",
    badge:      null,
    color:      "#ec4899",
    experience: "Just you and one other family. The priest can comfortably see and address both families directly — much more personal than a large group. Near-private at an accessible price.",
    includes: [
      "Live video puja",
      "Priest directly addresses both families",
      "Full name, gotra & sankalpa per family",
      "Conversation with priest throughout",
      "Prasad photo + blessings audio",
      "Recording access for 14 days",
    ],
  },
  {
    key:              "ekanta",
    label:            "Ekanta",
    slots:            1,
    slot_label:       "Only you — no one else",
    family_slot_label:"Your family only — no one else",
    tagline:          "The priest performs this puja for you and you alone",
    badge:      "Private",
    color:      "#8b5cf6",
    experience: "A completely private, fully dedicated session. Your name, your family, your sankalpa — nothing shared with anyone. The priest's complete presence, yours.",
    includes: [
      "Live video — only for you",
      "100% dedicated priest",
      "Full name, gotra, family names & occasion",
      "Custom sankalpa written & chanted",
      "Priest certificate of completion",
      "HD recording — lifetime access",
    ],
  },
];

// Keep TIERS alias for any legacy references
const TIERS = TIERS_BASE;

// ── Design tokens — warm temple palette ────────────────────────────────────────
const C = {
  bg:     "#030302",  // near-black warm brown — temple stone, not red
  bg2:    "#0A0900",  // card bg — dark amber, warm like brass lamp light
  bg3:    "#121000",  // deep card / input bg
  saffron:"#E8832A",
  gold:   "#C9953A",
  cream:  "#FFF5E0",
  text:   "#EDE0C8",
  dim:    "#8A7558",
  border: "rgba(200,140,30,0.25)",
};

// ── White catalog palette — used in all post-landing booking flow ──────────────
const CK = {
  pageBg:   "#FFFFFF",
  headerBg: "#FFFFFF",
  cardBg:   "#FFFFFF",
  cardBg2:  "#FFFFFF",
  gold:     "#E8832A",   // saffron accent — matches var(--saffron)
  glow:     "#E8832A",   // saffron accent (kept as literal hex — used with alpha-suffix concatenation)
  kumkum:   "#B03000",   // sparing secondary accent for Diksha/tradition differentiation only
  cream:    "#111111",   // headings — near-black, matches var(--text)
  muted:    "#111111",   // body text — near-black, matches var(--text)
  dim:      "#333333",   // secondary/meta text — matches var(--text-dim)
  border:   "#E3E3E3",   // matches var(--border)
  borderHot:"#E8832A",
};
const font = {
  title: "'Inter',sans-serif",
  body: "'Inter',sans-serif",
  sanskrit: "'Noto Sans Devanagari', serif",
};

// ── Atoms ──────────────────────────────────────────────────────────────────────
const Divider = () => (
  <div style={{ display:"flex", alignItems:"center", gap:"12px", margin:"24px 0" }}>
    <div style={{ flex:1, height:"1px", background:CK.border }} />
    <div style={{ width:7, height:7, background:CK.glow, transform:"rotate(45deg)" }} />
    <div style={{ flex:1, height:"1px", background:CK.border }} />
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontFamily:font.title, fontSize:"1.25rem", color:CK.gold,
    marginBottom:"16px", fontWeight:"800",
    textShadow:"0 0 20px rgba(232,131,42,0.18)",
    borderLeft:`3px solid ${CK.glow}`,
    paddingLeft:"12px" }}>{children}</div>
);

const Pill = ({ children, color }) => (
  <span style={{
    display:"inline-flex", alignItems:"center",
    background:`${color}18`, border:`1px solid ${color}44`,
    borderRadius:"20px", padding:"4px 11px",
    fontSize:"0.95rem", color, fontWeight:700, fontFamily:font.title,
  }}>{children}</span>
);

const BackBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    background:"transparent", border:`1px solid ${CK.border}`,
    borderRadius:"8px", padding:"8px 18px", color:CK.dim,
    fontFamily:font.body, fontSize:"0.95rem", cursor:"pointer",
    marginBottom:"24px", transition:"all 0.2s",
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = CK.glow; e.currentTarget.style.color = CK.glow; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = CK.border; e.currentTarget.style.color = CK.dim; }}
  >← Back</button>
);

const PrimaryBtn = ({ children, disabled, style, ...props }) => (
  <button style={{
    background: "var(--saffron)",
    border:"none", borderRadius:"10px", padding:"13px 28px",
    color:"white", fontFamily:font.title, fontSize:"1rem", fontWeight:700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1,
    transition:"opacity 0.2s", width:"100%", ...style,
  }} disabled={disabled} {...props}>{children}</button>
);

const FieldLabel = ({ children }) => (
  <div style={{ fontSize:"0.95rem", color:CK.muted, marginBottom:"6px",
    fontWeight:"600" }}>{children}</div>
);

const inputStyle = {
  width:"100%", background:CK.cardBg, border:`1px solid ${CK.border}`,
  borderRadius:"8px", padding:"11px 14px", color:CK.cream,
  fontFamily:font.body, fontSize:"1rem", outline:"none",
  boxShadow:"none",
};

// ── Diya Flame (utility — used in GuestBookingGate modal) ─────────────────────
function DiyaFlame({ size = 1, style: extStyle = {} }) {
  const W = size;
  const brassBg = `linear-gradient(90deg, #4A3008 0%, #8B4A10 18%, #C9953A 42%, #FFD97D 56%, #C9953A 72%, #8B4A10 88%, #4A3008 100%)`;
  return (
    <div style={{ display:"inline-block", animation:"lampSway 3.5s ease-in-out infinite", ...extStyle }}>
      <div style={{ position:"relative", display:"flex", flexDirection:"column", alignItems:"center" }}>
        <div style={{ position:"absolute", top:-8*W, left:"50%", transform:"translateX(-50%)", width:38*W, height:48*W,
          background:"radial-gradient(ellipse at 50% 70%, rgba(255,120,0,0.45) 0%, transparent 70%)",
          animation:"flamePulse 1.8s ease-in-out infinite", pointerEvents:"none" }} />
        <div style={{ width:16*W, height:30*W, background:"radial-gradient(ellipse at 50% 85%, #FF8C00 0%, #FF4500 55%, rgba(255,40,0,0.3) 100%)",
          borderRadius:"50% 50% 20% 20% / 60% 60% 40% 40%", animation:"flameFlicker 1.4s ease-in-out infinite",
          position:"relative", zIndex:2, filter:"drop-shadow(0 0 5px rgba(255,140,0,0.7))" }}>
          <div style={{ position:"absolute", bottom:3*W, left:"50%", transform:"translateX(-50%)", width:8*W, height:16*W,
            background:"radial-gradient(ellipse at 50% 85%, #FFEF00, #FFD700 60%)",
            borderRadius:"50% 50% 20% 20% / 60% 60% 40% 40%", animation:"flameFlicker 1s ease-in-out infinite reverse" }} />
        </div>
        <div style={{ width:3*W, height:6*W, background:"#3D2000", borderRadius:2, zIndex:3 }} />
        <div style={{ width:52*W, height:18*W, background:brassBg, borderRadius:"0 0 60% 60% / 0 0 100% 100%",
          boxShadow:"inset 0 3px 6px rgba(0,0,0,0.4), 0 2px 8px rgba(139,74,16,0.4)", position:"relative", zIndex:3 }}>
          <div style={{ position:"absolute", top:4*W, left:"22%", right:"22%", height:5*W,
            background:"radial-gradient(ellipse, rgba(255,200,60,0.35), transparent)", borderRadius:"50%" }} />
        </div>
        <div style={{ width:16*W, height:5*W, background:"linear-gradient(180deg, #C97A20, #8B4A10)", borderRadius:"0 0 4px 4px" }} />
      </div>
    </div>
  );
}

// ── KuthuVilakku — South Indian brass temple lamp ─────────────────────────────
function KuthuVilakku({ size = 1 }) {
  const W = size;
  const brassBg = `linear-gradient(90deg, #3A2004 0%, #7A4A0C 15%, #B8720A 30%, #C9953A 44%, #FFD97D 54%, #C9953A 66%, #B8720A 78%, #7A4A0C 90%, #3A2004 100%)`;
  const tier = (w, h, extra = {}) => ({
    width:w*W, height:h*W, background:brassBg,
    borderRadius:`${h*W * 0.45}px`,
    boxShadow:`0 2px 8px rgba(0,0,0,0.55), 0 0 6px rgba(201,149,58,0.18)`,
    flexShrink:0, ...extra,
  });
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", position:"relative", animation:"brassGlow 3.5s ease-in-out infinite" }}>
      {/* Smoke wisps */}
      <div style={{ position:"absolute", top:18*W, left:"50%", transform:"translateX(-50%)", zIndex:0, width:1 }}>
        {[-16, 0, 16].map((ox, i) => (
          <div key={i} style={{
            position:"absolute", left:ox*W - 1,
            width:3*W, height:28*W,
            background:`linear-gradient(to top, rgba(200,170,130,0.28), transparent)`,
            borderRadius:4, animation:`smokeRise ${3.8 + i*0.6}s ease-out infinite`,
            animationDelay:`${i * 0.9}s`,
          }} />
        ))}
      </div>
      {/* 3 flames */}
      <div style={{ display:"flex", gap:20*W, marginBottom:3*W, zIndex:2, position:"relative" }}>
        {[0.65, 1, 0.65].map((s, i) => (
          <div key={i} style={{
            width:13*W*s, height:22*W*s,
            background:"radial-gradient(ellipse at 50% 85%, #FF9500 0%, #FF4500 50%, rgba(255,40,0,0.25) 100%)",
            borderRadius:"50% 50% 20% 20% / 60% 60% 40% 40%",
            animation:`flameFlicker ${1.2 + i*0.18}s ease-in-out infinite`,
            animationDelay:`${i*0.28}s`,
            filter:"drop-shadow(0 0 6px rgba(255,150,0,0.85))",
            position:"relative",
          }}>
            <div style={{ position:"absolute", bottom:2*W*s, left:"50%", transform:"translateX(-50%)",
              width:6*W*s, height:12*W*s,
              background:"radial-gradient(ellipse at 50% 85%, #FFEF00, #FFD700 60%)",
              borderRadius:"50% 50% 20% 20% / 60% 60% 40% 40%",
              animation:`flameFlicker ${0.85 + i*0.15}s ease-in-out infinite reverse`,
            }} />
          </div>
        ))}
      </div>
      {/* Oil tray */}
      <div style={tier(62, 11)} />
      {/* Arms */}
      <div style={{ position:"relative", width:62*W, height:16*W, flexShrink:0 }}>
        <div style={{ position:"absolute", left:0, top:"50%", transform:"translateY(-50%)", width:20*W, height:4*W, background:brassBg, borderRadius:2*W }} />
        <div style={{ position:"absolute", right:0, top:"50%", transform:"translateY(-50%)", width:20*W, height:4*W, background:brassBg, borderRadius:2*W }} />
        <div style={{ position:"absolute", left:"50%", transform:"translateX(-50%)", width:8*W, height:16*W, background:brassBg, borderRadius:2 }} />
      </div>
      {/* Neck */}
      <div style={tier(20, 13)} />
      <div style={{ width:7*W, height:16*W, background:brassBg, borderRadius:2, flexShrink:0 }} />
      {/* Mid tier */}
      <div style={tier(38, 15)} />
      <div style={tier(26, 9)} />
      <div style={{ width:7*W, height:20*W, background:brassBg, borderRadius:2, flexShrink:0 }} />
      {/* Base tier */}
      <div style={tier(54, 18)} />
      <div style={tier(76, 15)} />
      {/* Ground plate */}
      <div style={{
        width:92*W, height:9*W,
        background:`linear-gradient(180deg, #C9953A 0%, #7A4A0C 50%, #3A2004 100%)`,
        borderRadius:`${4*W}px`, boxShadow:`0 4px 18px rgba(0,0,0,0.65)`, flexShrink:0,
      }} />
    </div>
  );
}

// ── Oil Diya — realistic terracotta oil lamp ──────────────────────────────────
function OilDiya({ size = 1, animDelay = 0, tiltDeg = 0 }) {
  const s = size;
  const delay = `${animDelay}s`;
  return (
    <div style={{
      display: "inline-block",
      transform: `rotate(${tiltDeg}deg)`,
      transformOrigin: "center bottom",
    }}>
      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Flame glow halo */}
        <div style={{
          position: "absolute",
          bottom: 32*s, right: 6*s,
          width: 44*s, height: 44*s,
          background: "radial-gradient(ellipse, rgba(255,140,0,0.55) 0%, rgba(255,100,0,0.2) 40%, transparent 70%)",
          animation: "flamePulse 2s ease-in-out infinite",
          animationDelay: delay,
          borderRadius: "50%",
          pointerEvents: "none",
        }} />
        {/* Flame */}
        <div style={{
          position: "absolute",
          bottom: 34*s, right: 3*s,
          width: 10*s, height: 24*s,
          background: "radial-gradient(ellipse at 50% 88%, #FFA500 0%, #FF4500 55%, rgba(255,50,0,0.2) 100%)",
          borderRadius: "50% 50% 25% 25% / 65% 65% 35% 35%",
          animation: `flameFlicker 1.3s ease-in-out infinite`,
          animationDelay: delay,
          filter: "drop-shadow(0 0 6px rgba(255,140,0,0.9)) drop-shadow(0 0 14px rgba(255,100,0,0.5))",
          zIndex: 5,
        }}>
          {/* Inner bright core */}
          <div style={{
            position: "absolute", bottom: "12%", left: "50%", transform: "translateX(-50%)",
            width: 4*s, height: 13*s,
            background: "radial-gradient(ellipse at 50% 88%, #FFFFFF 0%, #FFEF88 55%, transparent 100%)",
            borderRadius: "50% 50% 20% 20% / 60% 60% 40% 40%",
            animation: `flameFlicker 0.9s ease-in-out infinite reverse`,
            animationDelay: delay,
          }} />
        </div>
        {/* Wick — slightly angled toward spout */}
        <div style={{
          position: "absolute",
          bottom: 24*s, right: 5*s,
          width: 2*s, height: 13*s,
          background: "linear-gradient(180deg, #2A1000, #5A2800)",
          borderRadius: "1px 1px 0 0",
          transform: "rotate(8deg)",
          transformOrigin: "bottom center",
          zIndex: 4,
        }} />
        {/* Diya bowl */}
        <div style={{ position: "relative", width: 64*s, height: 28*s, zIndex: 3 }}>
          {/* Main body — terracotta gradient */}
          <div style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 28*s,
            background: `
              radial-gradient(ellipse at 38% 28%, #EFA060 0%, #C86C2A 38%, #8C3E14 75%, #5A2208 100%)
            `,
            borderRadius: "50% 50% 50% 50% / 55% 55% 45% 45%",
            boxShadow: `
              inset 0 -5px 10px rgba(0,0,0,0.45),
              inset 0 5px 10px rgba(255,190,100,0.18),
              0 6px 16px rgba(0,0,0,0.55),
              0 0 18px rgba(180,90,20,0.12)
            `,
            overflow: "hidden",
          }}>
            {/* Oil inside — dark pool */}
            <div style={{
              position: "absolute",
              top: "22%", left: "14%", right: "22%", height: "35%",
              background: "radial-gradient(ellipse, rgba(140,80,20,0.75) 0%, rgba(60,25,5,0.92) 100%)",
              borderRadius: "50%",
            }} />
            {/* Oil surface sheen */}
            <div style={{
              position: "absolute",
              top: "24%", left: "28%", width: "20%", height: "10%",
              background: "radial-gradient(ellipse, rgba(255,200,100,0.25), transparent)",
              borderRadius: "50%",
            }} />
            {/* Rim highlight */}
            <div style={{
              position: "absolute",
              top: "5%", left: "8%", right: "12%", height: "8%",
              background: "linear-gradient(90deg, transparent, rgba(255,190,110,0.22), transparent)",
              borderRadius: "50%",
            }} />
          </div>
          {/* Spout / nose — pointed wick end */}
          <div style={{
            position: "absolute",
            right: -10*s, top: "12%",
            width: 20*s, height: 15*s,
            background: "radial-gradient(ellipse at 15% 45%, #D07030, #8C3E14 60%, #5A2208 100%)",
            borderRadius: "0 65% 65% 0",
            boxShadow: "2px 2px 6px rgba(0,0,0,0.45)",
            transform: "rotate(-6deg)",
            transformOrigin: "left center",
          }} />
        </div>
        {/* Ground pool of light */}
        <div style={{
          width: 56*s, height: 6*s,
          background: "radial-gradient(ellipse, rgba(255,140,0,0.18) 0%, transparent 80%)",
          borderRadius: "50%",
          marginTop: 3*s,
        }} />
      </div>
    </div>
  );
}


// ── Rangoli Divider ────────────────────────────────────────────────────────────
function RangoliDivider() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:14, margin:"28px 0" }}>
      <div style={{ flex:1, height:1, background:"linear-gradient(90deg, transparent, rgba(201,149,58,0.45), rgba(201,149,58,0.2))" }} />
      <div style={{ position:"relative", width:34, height:34 }}>
        {[0, 45, 90, 135].map(deg => (
          <div key={deg} style={{
            position:"absolute", top:"50%", left:"50%",
            transform:`translate(-50%,-50%) rotate(${deg}deg)`,
            width:26, height:9, background:"rgba(201,149,58,0.45)", borderRadius:"50%",
          }} />
        ))}
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
          width:7, height:7, background:C.saffron, borderRadius:"50%", boxShadow:`0 0 8px ${C.saffron}88` }} />
      </div>
      <div style={{ flex:1, height:1, background:"linear-gradient(90deg, rgba(201,149,58,0.2), rgba(201,149,58,0.45), transparent)" }} />
    </div>
  );
}

// ── Temple bell FM synthesis — must fire on user gesture (browser audio policy) ──
function playTempleBell() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [[330,0.42,5.5],[660,0.16,3.8],[220,0.28,6.5],[990,0.07,2.4],[1650,0.03,1.2]].forEach(([freq,amp,dur]) => {
      const carrier  = ctx.createOscillator();
      const mod      = ctx.createOscillator();
      const modGain  = ctx.createGain();
      const envGain  = ctx.createGain();
      carrier.type = mod.type = "sine";
      carrier.frequency.value = freq;
      mod.frequency.value     = freq * 2.756;
      modGain.gain.setValueAtTime(freq * 4.5, now);
      modGain.gain.exponentialRampToValueAtTime(0.5, now + dur * 0.55);
      envGain.gain.setValueAtTime(0, now);
      envGain.gain.linearRampToValueAtTime(amp, now + 0.004);
      envGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      mod.connect(modGain);
      modGain.connect(carrier.frequency);
      carrier.connect(envGain);
      envGain.connect(ctx.destination);
      carrier.start(now); carrier.stop(now + dur);
      mod.start(now);     mod.stop(now + dur);
    });
    return ctx;
  } catch(e) { return null; }
}
function playOmDrone(ctx) {
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    [110, 110.4, 109.7, 220, 165].forEach((f, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      const amp  = i < 3 ? 0.07 : 0.035;
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0, now + 0.9);
      gain.gain.linearRampToValueAtTime(amp, now + 2.2);
      gain.gain.setValueAtTime(amp, now + 4.0);
      gain.gain.linearRampToValueAtTime(0, now + 6.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + 0.9);
      osc.stop(now + 7);
    });
  } catch(e) {}
}

// ── Guest Puja Landing — immersive temple entrance for non-logged-in users ──────
function GuestPujaLanding({ onEnter }) {
  const [need, setNeed]           = useState(null);
  const [hovNeed, setHovNeed]     = useState(null);
  // Bell ceremony: gate → ringing → om → fading → done
  const [bellPhase, setBellPhase] = useState("gate");

  const handleRingBell = () => {
    const ctx = playTempleBell();
    setBellPhase("ringing");
    setTimeout(() => { setBellPhase("om"); playOmDrone(ctx); }, 900);
    setTimeout(() => setBellPhase("fading"), 3200);
    setTimeout(() => setBellPhase("done"), 4000);
  };

  const today   = DAY_PUJA[new Date().getDay()];
  const needPujas = need
    ? PUJAS.filter(p => NEEDS.find(n => n.id === need)?.pujas.includes(p.id))
    : [];
  const minPujaPrice = (p) => {
    const tier   = p.pricing_tier || p.category || "simple";
    const prices = PRICING[tier] || PRICING.simple;
    return prices.satsang || prices.ekanta || 151;
  };

  return (
    <div style={{ fontFamily:font.body, overflowX:"hidden" }}>
      <style>{SACRED_ANIM_CSS}</style>

      {/* ══ BELL CARD — centered modal, dim backdrop ════════════════════════════ */}
      {bellPhase !== "done" && (
        <div style={{
          position:"fixed", inset:0, zIndex:8000,
          display:"flex", alignItems:"center", justifyContent:"center",
          background: bellPhase === "fading" ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.58)",
          backdropFilter: bellPhase === "fading" ? "blur(0px)" : "blur(3px)",
          transition:"background 0.9s ease-out, backdrop-filter 0.9s ease-out",
        }}>

          {/* Card */}
          <div style={{
            width:"min(380px, 90vw)",
            background:"#FFFFFF",
            border:"1px solid var(--border)",
            borderRadius:"14px",
            padding:"40px 32px 32px",
            textAlign:"center",
            boxShadow:"0 24px 60px rgba(20,20,20,0.18)",
            opacity: bellPhase === "fading" ? 0 : 1,
            transform: bellPhase === "fading" ? "scale(0.96)" : "scale(1)",
            transition:"opacity 0.9s ease-out, transform 0.9s ease-out",
            position:"relative", overflow:"hidden",
          }}>

            {/* Label */}
            <div style={{
              fontFamily:font.title, fontWeight:700,
              fontSize:"0.95rem",
              color:"var(--text-dim)", marginBottom:"6px",
            }}>Vedsanga · Puja</div>
            <div style={{
              fontFamily:font.body, fontSize:"0.95rem",
              color:"var(--text-dim)", marginBottom:"28px",
            }}>Live priest · from anywhere in the world</div>

            {/* GATE */}
            {bellPhase === "gate" && (
              <div style={{ position:"relative", zIndex:1 }}>
                {/* SVG ghanta */}
                <div style={{ display:"inline-block", marginBottom:"20px" }}>
                  <svg width="68" height="84" viewBox="0 0 72 88" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="36" cy="7" rx="5" ry="3.5" stroke="#B8843C" strokeWidth="1.5" fill="rgba(232,131,42,0.12)"/>
                    <rect x="33" y="9" width="6" height="5" rx="1" fill="rgba(232,131,42,0.14)" stroke="#B8843C" strokeWidth="1"/>
                    <path d="M36 13 C24 13 13 24 12 38 L10 56 L62 56 L60 38 C59 24 48 13 36 13Z"
                      fill="rgba(232,131,42,0.08)" stroke="#B8843C" strokeWidth="1.2"/>
                    <path d="M36 17 C26 17 18 27 17 40 L16 52"
                      stroke="rgba(232,131,42,0.35)" strokeWidth="9" strokeLinecap="round" fill="none"/>
                    <path d="M7 56 Q36 67 65 56" stroke="#B8843C" strokeWidth="1.5" fill="none"/>
                    <path d="M5 59 Q36 71 67 59" stroke="rgba(184,132,60,0.5)" strokeWidth="1" fill="none"/>
                    <line x1="36" y1="14" x2="36" y2="62" stroke="rgba(184,132,60,0.4)" strokeWidth="1"/>
                    <circle cx="36" cy="66" r="4" fill="rgba(232,131,42,0.15)" stroke="#B8843C" strokeWidth="1"/>
                  </svg>
                </div>

                <div style={{
                  fontFamily:"'Noto Sans Devanagari',serif",
                  fontSize:"1rem", color:"#8A6D3B",
                  letterSpacing:"0.03em", marginBottom:"28px",
                }}>ॐ नमः शिवाय</div>

                <button onClick={handleRingBell} style={{
                  width:"100%",
                  background:C.saffron,
                  border:"none",
                  borderRadius:"10px", padding:"14px 0",
                  color:"#FFFFFF",
                  fontFamily:font.title, fontWeight:700,
                  fontSize:"1rem",
                  cursor:"pointer", transition:"all 0.2s",
                  boxShadow:"0 4px 16px rgba(232,131,42,0.35)",
                }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                >
                  Ring the Bell
                </button>

                <div style={{ marginTop:"20px" }}>
                  <span onClick={() => setBellPhase("done")} style={{
                    fontSize:"0.95rem", color:"var(--text-dim)",
                    fontFamily:font.body,
                    cursor:"pointer", borderBottom:"1px solid var(--border)",
                    paddingBottom:"1px", transition:"color 0.2s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.color = "var(--text)"}
                    onMouseLeave={e => e.currentTarget.style.color = "var(--text-dim)"}
                  >skip →</span>
                </div>
              </div>
            )}

            {/* RINGING / OM */}
            {(bellPhase === "ringing" || bellPhase === "om" || bellPhase === "fading") && (
              <div style={{ position:"relative", zIndex:1, padding:"8px 0" }}>
                <div style={{
                  display:"inline-block", marginBottom:"16px",
                  animation: bellPhase === "ringing" ? "bell-ring 1.1s ease-out" : "none",
                }}>
                  <svg width="52" height="64" viewBox="0 0 72 88" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="36" cy="7" rx="5" ry="3.5" stroke="#B8843C" strokeWidth="1.5" fill="rgba(232,131,42,0.18)"/>
                    <rect x="33" y="9" width="6" height="5" rx="1" fill="rgba(232,131,42,0.2)" stroke="#B8843C" strokeWidth="1"/>
                    <path d="M36 13 C24 13 13 24 12 38 L10 56 L62 56 L60 38 C59 24 48 13 36 13Z"
                      fill="rgba(232,131,42,0.14)" stroke="#B8843C" strokeWidth="1.2"/>
                    <path d="M36 17 C26 17 18 27 17 40 L16 52"
                      stroke="rgba(232,131,42,0.4)" strokeWidth="9" strokeLinecap="round" fill="none"/>
                    <path d="M7 56 Q36 67 65 56" stroke="#B8843C" strokeWidth="1.5" fill="none"/>
                    <path d="M5 59 Q36 71 67 59" stroke="rgba(184,132,60,0.55)" strokeWidth="1" fill="none"/>
                    <line x1="36" y1="14" x2="36" y2="62" stroke="rgba(184,132,60,0.5)" strokeWidth="1"/>
                    <circle cx="36" cy="66" r="4" fill="rgba(232,131,42,0.22)" stroke="#B8843C" strokeWidth="1"/>
                  </svg>
                </div>

                <div style={{
                  fontFamily:"'Noto Sans Devanagari',serif",
                  fontSize:"clamp(3.2rem,9vw,5rem)",
                  color: bellPhase === "ringing" ? "rgba(232,131,42,0)" : C.saffron,
                  transition:"color 0.7s ease-in", lineHeight:1, marginBottom:"14px",
                }}>ॐ</div>

                <div style={{
                  fontFamily:"'Noto Sans Devanagari',serif", fontSize:"1rem",
                  color: bellPhase === "om" || bellPhase === "fading"
                    ? "#8A6D3B" : "transparent",
                  transition:"color 0.9s",
                }}>हर हर महादेव</div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ══ END BELL CARD ════════════════════════════════════════════════════════ */}

      {/* ── HERO — puja introduction ── */}
      <div style={{
        position:"relative", overflow:"hidden",
        background:"#FFFFFF",
        padding:"0 24px 44px", textAlign:"center",
        minHeight:460,
      }}>

        {/* All content */}
        <div style={{ position:"relative", zIndex:2 }}>

          {/* Large OM — hero visual */}
          <div style={{ paddingTop:36, marginBottom:8 }}>
            <div style={{
              fontFamily:font.sanskrit, fontSize:"5rem", color:C.saffron, lineHeight:1,
              animation:"sacredFadeIn 1s ease-out",
            }}>ॐ</div>
          </div>

          {/* Headline */}
          <div style={{
            fontFamily:font.title, fontWeight:700, fontSize:"1.8rem", color:"var(--text)",
            marginBottom:10, lineHeight:1.3,
            animation:"sacredFadeIn 1s ease-out 0.2s both",
          }}>Your Puja, Live with a Priest</div>

          <div style={{
            fontFamily:font.body,
            fontSize:"1.05rem", color:"var(--text-dim)",
            maxWidth:360, margin:"0 auto 22px", lineHeight:1.75,
            animation:"sacredFadeIn 1s ease-out 0.35s both",
          }}>
            A verified Vedic priest performs your puja live.
            Your name read aloud in the ritual — from anywhere in the world.
          </div>

          {/* Today's shubh puja */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:14,
            background:"#FFFFFF",
            border:"1px solid var(--border)",
            borderRadius:18, padding:"13px 20px",
            marginBottom:26, cursor:"pointer",
            animation:"sacredFadeIn 1s ease-out 0.5s both",
            transition:"all 0.2s",
            boxShadow:"var(--card-shadow)",
          }}
            onClick={onEnter}
            onMouseOver={e => { e.currentTarget.style.borderColor = C.saffron; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
          >
            <span style={{ fontSize:"1.7rem" }}>{today.emoji}</span>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:"0.95rem", color:"var(--text-dim)", fontWeight:600, marginBottom:3 }}>
                Aaj {today.day} · Shubh Din
              </div>
              <div style={{ fontFamily:font.title, fontSize:"1.05rem", color:"var(--text)", fontWeight:600, marginBottom:2 }}>
                {today.name}
              </div>
              <div style={{ fontFamily:font.sanskrit, fontSize:"0.95rem", color:C.saffron }}>
                {today.mantra}
              </div>
            </div>
            <div style={{
              background:"rgba(232,131,42,0.12)", border:`1px solid ${C.saffron}66`,
              borderRadius:10, padding:"7px 14px", flexShrink:0,
              color:C.saffron, fontSize:"0.95rem", fontFamily:font.body, fontWeight:700,
            }}>Book →</div>
          </div>

          {/* Social proof */}
          <div style={{
            display:"flex", gap:36, justifyContent:"center",
            animation:"sacredFadeIn 1s ease-out 0.65s both",
            marginBottom: 36,
          }}>
            {[["12,000+","Devotees"],["847","Pujas performed"],["38","Verified Priests"]].map(([n,l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.2rem", color:C.saffron }}>{n}</div>
                <div style={{ fontSize:"0.95rem", color:"var(--text-dim)" }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Three oil diyas — sacred light at base of hero */}
          <div style={{
            display:"flex", justifyContent:"center", alignItems:"flex-end",
            gap:28, paddingBottom:8,
            animation:"sacredFadeIn 1s ease-out 0.9s both",
          }}>
            <OilDiya size={0.85} animDelay={0.4} tiltDeg={-6} />
            <OilDiya size={1.15} animDelay={0}   tiltDeg={0} />
            <OilDiya size={0.85} animDelay={0.7} tiltDeg={6} />
          </div>
          {/* Warm light pool below diyas */}
          <div style={{
            height:1,
            background:`linear-gradient(90deg, transparent, rgba(232,131,42,0.35), rgba(201,149,58,0.5), rgba(232,131,42,0.35), transparent)`,
            margin:"0 auto", maxWidth:260,
          }} />
        </div>
      </div>

      {/* ── Saffron border transition ── */}
      <div style={{ height:3, background:`linear-gradient(90deg, transparent, ${C.saffron}88, ${C.gold}99, ${C.saffron}88, transparent)` }} />

      {/* ── WHAT BRINGS YOU HERE ── */}
      <div style={{
        background:"#FFFFFF",
        padding:"36px 20px 8px", position:"relative",
      }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:20, fontFamily:font.title, fontWeight:700, fontSize:"1.2rem", color:"var(--text)" }}>
            What brings you here today?
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:24 }}>
            {NEEDS.map(n => {
              const active = need === n.id;
              const hov    = hovNeed === n.id;
              return (
                <button key={n.id}
                  onClick={() => setNeed(need === n.id ? null : n.id)}
                  onMouseEnter={() => setHovNeed(n.id)}
                  onMouseLeave={() => setHovNeed(null)}
                  style={{
                    background: active ? "rgba(232,131,42,0.08)" : "#FFFFFF",
                    border:`1px solid ${active ? C.saffron : hov ? C.saffron+"88" : "var(--border)"}`,
                    borderRadius:14, padding:"15px 7px",
                    cursor:"pointer", textAlign:"center", transition:"all 0.2s",
                    transform: active ? "translateY(-3px)" : "none",
                    boxShadow: active ? "var(--card-shadow-hover)" : "var(--card-shadow)",
                  }}>
                  <div style={{ fontSize:"1.5rem", marginBottom:6 }}>{n.icon}</div>
                  <div style={{ fontFamily:font.title, fontWeight:600, fontSize:"0.95rem", color: active ? C.saffron : "var(--text)" }}>
                    {n.label}
                  </div>
                  <div style={{ fontFamily:font.sanskrit, fontSize:"0.95rem", color:"var(--text-dim)", marginTop:3 }}>
                    {n.hindi}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Recommended pujas for chosen need */}
          {need && needPujas.length > 0 && (
            <div style={{ marginBottom:24, animation:"guestNeedPop 0.35s ease-out" }}>
              <div style={{ fontSize:"1rem", color:"var(--text-dim)", fontWeight:600, marginBottom:12, textAlign:"center" }}>
                Recommended for you
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {needPujas.map(p => (
                  <button key={p.id} onClick={onEnter}
                    style={{
                      background:"#FFFFFF",
                      border:`1px solid var(--border)`,
                      borderRadius:14, padding:"18px 14px",
                      textAlign:"left", cursor:"pointer", transition:"all 0.2s",
                      boxShadow:"var(--card-shadow)",
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
                  >
                    <div style={{ fontSize:"1.9rem", marginBottom:8 }}>{p.emoji}</div>
                    <div style={{ fontFamily:font.title, fontWeight:600, fontSize:"1.05rem", color:"var(--text)", marginBottom:4 }}>{p.name}</div>
                    <div style={{ fontFamily:font.sanskrit, fontSize:"0.95rem", color:"var(--text-dim)", marginBottom:10 }}>{p.sanskrit}</div>
                    <div style={{ fontSize:"1rem", color:C.saffron, fontWeight:700 }}>
                      from ₹{minPujaPrice(p).toLocaleString("en-IN")} →
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{ background:"#FFFFFF", padding:"4px 20px 8px" }}>
        <div style={{ maxWidth:580, margin:"0 auto" }}>
          <RangoliDivider />

          <div style={{
            background:"#FFFFFF",
            border:`1px solid var(--border)`,
            borderRadius:18, padding:"24px 20px", marginBottom:20,
            boxShadow:"var(--card-shadow)",
          }}>
            <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.1rem", color:"var(--text)", marginBottom:18, textAlign:"center" }}>
              How It Works
            </div>
            {[
              ["🪔","Choose your deity & need",    "Browse pujas by deity, occasion, or what you seek"],
              ["🧑‍🙏","Pick your session type",   "Group of 10, circle of 5, or a private Ekanta session"],
              ["🎥","Pray live with the priest",   "Your name and sankalpa read aloud — you participate from home"],
              ["📸","Receive prasad photo",         "A blessed prasad photo is shared with you after the puja"],
            ].map(([icon, title, desc]) => (
              <div key={title} style={{ display:"flex", gap:14, marginBottom:15, alignItems:"flex-start" }}>
                <span style={{ fontSize:"1.3rem", flexShrink:0 }}>{icon}</span>
                <div>
                  <div style={{ fontSize:"1rem", color:"var(--text)", marginBottom:2, fontWeight:600 }}>{title}</div>
                  <div style={{ fontSize:"0.95rem", color:"var(--text-dim)", lineHeight:1.65 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center", marginBottom:24 }}>
            {[["🎥","Live video"],["🕯️","Verified priests"],["🔒","Secure"],["📿","Vedic rituals"]].map(([icon, label]) => (
              <div key={label} style={{
                display:"flex", alignItems:"center", gap:6,
                background:"#FFFFFF", border:`1px solid var(--border)`,
                borderRadius:20, padding:"6px 14px",
                fontSize:"0.95rem", color:"var(--text-dim)",
              }}>
                <span>{icon}</span><span>{label}</span>
              </div>
            ))}
          </div>

          {/* Main CTA */}
          <div style={{ textAlign:"center", paddingBottom:44 }}>
            <PrimaryBtn onClick={onEnter} style={{ maxWidth:320, margin:"0 auto" }}>
              🪔 Enter the Temple — Browse Pujas
            </PrimaryBtn>
            <div style={{ fontSize:"0.95rem", color:"var(--text-dim)", marginTop:10 }}>
              Free to browse · Sign in only when you book
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Guest Booking Gate — modal when guest tries to book ────────────────────────
function GuestBookingGate({ puja, onLogin, onClose }) {
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.9)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:9999, padding:"20px",
    }}>
      <style>{SACRED_ANIM_CSS}</style>
      <div style={{
        background:CK.cardBg,
        border:`1px solid ${CK.border}`,
        borderRadius:"24px", padding:"44px 32px",
        maxWidth:"380px", width:"100%", textAlign:"center",
        animation:"sacredFadeIn 0.4s ease-out",
        boxShadow:"var(--card-shadow-hover)",
      }}>
        {/* Diya */}
        <div style={{ marginBottom:"20px" }}>
          <DiyaFlame size={1.2} />
        </div>

        {/* Selected puja badge */}
        {puja && (
          <div style={{
            display:"inline-flex", alignItems:"center", gap:"10px",
            background:CK.cardBg, border:"1px solid var(--border)",
            borderRadius:"12px", padding:"8px 18px", marginBottom:"22px",
          }}>
            <span style={{ fontSize:"1.4rem" }}>{puja.emoji}</span>
            <div>
              <div style={{ fontFamily:font.title, fontWeight:600, fontSize:"1rem", color:CK.cream }}>{puja.name}</div>
              <div style={{ fontFamily:font.sanskrit, fontSize:"0.95rem", color:CK.dim }}>{puja.sanskrit}</div>
            </div>
          </div>
        )}

        <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.3rem", color:CK.cream, marginBottom:"10px" }}>
          Your Sacred Moment Awaits
        </div>
        <div style={{ fontFamily:font.body, fontSize:"1.08rem", color:CK.muted, lineHeight:1.7, marginBottom:"22px" }}>
          Create your free account to book.
          Your name and sankalpa will be read aloud by the priest during the ritual.
        </div>

        {/* Sanskrit blessing */}
        <div style={{
          fontFamily:font.sanskrit, fontSize:"1.05rem", color:CK.glow, marginBottom:"28px",
          filter:"drop-shadow(0 0 8px rgba(192,90,0,0.25))",
          animation:"goldenPulse 3s ease-in-out infinite",
        }}>
          ॐ सर्वे भवन्तु सुखिनः
        </div>

        <PrimaryBtn onClick={onLogin} style={{ marginBottom:"10px" }}>
          🙏 Join Free & Book This Puja
        </PrimaryBtn>
        <div style={{ fontSize:"0.95rem", color:CK.dim, marginBottom:"18px" }}>
          1 minute signup · No credit card · 100% free
        </div>

        <button onClick={onClose} style={{
          background:"transparent", border:`1px solid ${CK.border}`,
          borderRadius:"8px", padding:"8px 24px",
          color:CK.dim, fontFamily:font.body, fontSize:"0.95rem", cursor:"pointer",
          transition:"border-color 0.2s",
        }}
          onMouseOver={e => e.currentTarget.style.borderColor = CK.glow}
          onMouseOut={e => e.currentTarget.style.borderColor = CK.border}
        >
          ← Browse more pujas
        </button>
      </div>
    </div>
  );
}

// ── Personalisation Modal ──────────────────────────────────────────────────────
function PersonalisationModal({ onSave }) {
  const [state, setState]           = useState("");
  const [tradition, setTradition]   = useState("all");

  const handleSave = () => {
    if (!state) return;
    const stateObj = INDIA_STATES.find(s => s.value === state);
    const lang = stateObj ? stateObj.lang : "hindi";
    const profile = { state, tradition, language: lang };
    localStorage.setItem("veda_puja_profile", JSON.stringify(profile));
    onSave(profile);
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.82)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:999, padding:"20px",
    }}>
      <div style={{
        background:CK.cardBg,
        border:`1px solid ${CK.border}`, borderRadius:"20px",
        padding:"32px 28px", width:"100%", maxWidth:"400px",
        boxShadow:"var(--card-shadow-hover)",
      }}>
        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:"28px" }}>
          <div style={{ fontSize:"2.4rem", marginBottom:"10px" }}>🪔</div>
          <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.25rem", color:CK.glow, marginBottom:"6px" }}>
            Personalise Your Puja
          </div>
          <div style={{ fontFamily:font.body, fontSize:"1.08rem", color:CK.muted, lineHeight:"1.6" }}>
            We'll show pujas in your regional tradition and language.
          </div>
        </div>

        {/* State */}
        <div style={{ marginBottom:"20px" }}>
          <FieldLabel>Your State</FieldLabel>
          <select
            value={state}
            onChange={e => setState(e.target.value)}
            style={{ ...inputStyle, cursor:"pointer" }}
          >
            <option value="">— Select state —</option>
            {INDIA_STATES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {state && (
            <div style={{ fontSize:"0.95rem", color:CK.glow, marginTop:"5px" }}>
              {LANG_LABELS[INDIA_STATES.find(s2 => s2.value === state)?.lang]} pujas will be shown
            </div>
          )}
        </div>

        {/* Tradition */}
        <div style={{ marginBottom:"28px" }}>
          <FieldLabel>Your Tradition (optional)</FieldLabel>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            {TRADITIONS.map(t => (
              <button key={t.value} onClick={() => setTradition(t.value)} style={{
                background: tradition === t.value ? "rgba(232,131,42,0.1)" : CK.cardBg2,
                border:`1px solid ${tradition === t.value ? CK.glow : CK.border}`,
                borderRadius:"8px", padding:"10px 14px", color: tradition === t.value ? CK.glow : CK.dim,
                fontFamily:font.body, fontSize:"1rem", cursor:"pointer",
                textAlign:"left", transition:"all 0.15s",
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        <PrimaryBtn onClick={handleSave} disabled={!state}>
          Show My Pujas →
        </PrimaryBtn>

        <button onClick={() => {
          const def = { state: "other", tradition: "all", language: "hindi" };
          localStorage.setItem("veda_puja_profile", JSON.stringify(def));
          onSave(def);
        }} style={{
          display:"block", width:"100%", textAlign:"center",
          marginTop:"14px", background:"none", border:"none",
          color:CK.dim, fontSize:"0.95rem", cursor:"pointer", fontFamily:font.body,
        }}>Skip for now</button>
      </div>
    </div>
  );
}

// ── Pre-Puja Detail Page ───────────────────────────────────────────────────────
function PujaDetailStep({ puja, profile, onBack, onBook }) {
  const [detailData, setDetailData] = useState(null);
  const [checklist, setChecklist]   = useState({});
  const [loading, setLoading]       = useState(true);

  const lang = profile?.language || "hindi";
  const langCode = { hindi:"hi", telugu:"te", tamil:"ta", bengali:"bn", marathi:"mr" }[lang] || "en";

  useEffect(() => {
    // Fetch full detail from API (with regional variant applied)
    const state = profile?.state || "";
    fetch(`${API}/puja/catalog/${puja.id}?language=${langCode}&state=${state}`)
      .then(r => r.json())
      .then(d => { setDetailData(d); setLoading(false); })
      .catch(() => { setDetailData(puja); setLoading(false); });
  }, [puja.id, langCode, profile]);

  const data = detailData || puja;
  const displayName = data.display_name || (data.names && (data.names[langCode] || data.names.en)) || data.name || puja.name;
  const allItemsChecked = data.items_required
    ? data.items_required.every((_, i) => checklist[i])
    : true;

  if (loading) return (
    <div style={{ textAlign:"center", padding:"60px 0", color:CK.dim, background:CK.pageBg }}>
      <div style={{ fontSize:"2rem", marginBottom:"12px" }}>🪔</div>
      Loading puja details…
    </div>
  );

  return (
    <div style={{ background:CK.pageBg, minHeight:"60vh" }}>
      <style>{SACRED_ANIM_CSS}</style>

      {/* ── Deity Banner ── */}
      <div style={{
        position:"relative", overflow:"hidden",
        margin:"0 -32px 28px",
        background:"#FFFFFF",
        padding:"36px 32px 28px",
        textAlign:"center",
        borderBottom:`1px solid var(--border)`,
      }}>
        {/* Content */}
        <div style={{ position:"relative", zIndex:2 }}>
          <div style={{
            fontSize:"4rem", marginBottom:10,
          }}>{puja.emoji}</div>
          <div style={{
            fontFamily:font.title, fontWeight:700, fontSize:"1.5rem", color:"var(--text)",
            marginBottom:6,
          }}>{displayName}</div>
          {(puja.sanskrit || data.sanskrit_mantra) && (
            <div style={{
              fontFamily:font.sanskrit, fontSize:"1.05rem", color:"var(--saffron)",
              marginBottom:4,
            }}>{puja.sanskrit || data.sanskrit_mantra}</div>
          )}
          <div style={{ fontSize:"0.95rem", color:"var(--text-dim)", marginTop:6 }}>
            {(data.deity && (data.deity[langCode] || data.deity.en)) || puja.deity || ""}
            {data.duration_mins ? ` · ${data.duration_mins} min · Live video` : ""}
          </div>
        </div>
      </div>

      <BackBtn onClick={onBack} />

      {/* Quick info strip */}
      <div style={{
        display:"flex", gap:10, flexWrap:"wrap", marginBottom:"24px",
      }}>
        {[
          ["⏱", `${data.duration_mins || 45} min`],
          ["🎥", "Live video"],
          ...(data.best_day ? [["📅", data.best_day]] : []),
          ...(data.home_ok ? [["🏠", "From home"]] : []),
        ].map(([icon, label]) => (
          <div key={label} style={{
            display:"flex", alignItems:"center", gap:6,
            background:CK.cardBg, border:`1px solid ${CK.border}`,
            borderRadius:20, padding:"6px 14px",
            fontSize:"0.95rem", color:CK.dim,
          }}><span>{icon}</span><span>{label}</span></div>
        ))}
      </div>

      {/* Why do this puja */}
      {data.why_do_this && (
        <div style={{ marginBottom:"24px" }}>
          <SectionTitle>Why This Puja</SectionTitle>
          <div style={{
            background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
            borderRadius:"12px", padding:"16px 18px",
            fontSize:"1rem", color:CK.muted, lineHeight:"1.75",
          }}>{data.why_do_this}</div>
        </div>
      )}

      {/* Benefits */}
      {data.benefits && data.benefits.length > 0 && (
        <div style={{ marginBottom:"24px" }}>
          <SectionTitle>Benefits</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            {data.benefits.map((b, i) => (
              <div key={i} style={{ display:"flex", gap:"10px", alignItems:"flex-start" }}>
                <span style={{ color:CK.glow, fontSize:"0.95rem", marginTop:"2px" }}>✦</span>
                <span style={{ fontSize:"1rem", color:CK.muted, lineHeight:"1.6" }}>{b}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Divider />

      {/* Required Items — interactive checklist */}
      {data.items_required && data.items_required.length > 0 && (
        <div style={{ marginBottom:"24px" }}>
          <SectionTitle>
            Items to Arrange Before the Puja
            {" "}
            <span style={{ color:allItemsChecked ? "#15803d" : CK.dim, fontSize:"0.95rem" }}>
              {Object.values(checklist).filter(Boolean).length}/{data.items_required.length} ready
            </span>
          </SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            {data.items_required.map((item, i) => (
              <div key={i}
                onClick={() => setChecklist(prev => ({ ...prev, [i]: !prev[i] }))}
                style={{
                  display:"flex", gap:"12px", alignItems:"flex-start",
                  background: checklist[i] ? "rgba(34,197,94,0.08)" : CK.cardBg,
                  border:`1px solid ${checklist[i] ? "rgba(34,197,94,0.5)" : CK.border}`,
                  borderRadius:"10px", padding:"10px 14px", cursor:"pointer",
                  transition:"all 0.15s",
                }}>
                <div style={{
                  width:"18px", height:"18px", flexShrink:0,
                  borderRadius:"4px", marginTop:"1px",
                  background: checklist[i] ? "#22c55e" : "transparent",
                  border:`2px solid ${checklist[i] ? "#22c55e" : CK.dim}`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  {checklist[i] && <span style={{ color:"white", fontSize:"11px" }}>✓</span>}
                </div>
                <span style={{
                  fontSize:"1rem", lineHeight:"1.5",
                  color: checklist[i] ? "#166534" : CK.muted,
                  textDecoration: checklist[i] ? "line-through" : "none",
                }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prasad */}
      {data.prasad && (
        <div style={{ marginBottom:"24px" }}>
          <SectionTitle>Prasad to Prepare</SectionTitle>
          <div style={{
            background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
            borderRadius:"12px", padding:"16px 18px",
            fontSize:"1rem", color:CK.muted, lineHeight:"1.75",
          }}>
            <span style={{ marginRight:"8px" }}>🍚</span>{data.prasad}
          </div>
        </div>
      )}

      {/* How it works */}
      {data.how_it_works && data.how_it_works.length > 0 && (
        <div style={{ marginBottom:"24px" }}>
          <SectionTitle>How This Puja Works</SectionTitle>
          <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
            {data.how_it_works.map((step, i) => (
              <div key={i} style={{ display:"flex", gap:"12px", alignItems:"flex-start" }}>
                <div style={{
                  width:"24px", height:"24px", flexShrink:0,
                  borderRadius:"50%", background:"var(--bg3)",
                  border:"1px solid var(--border)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"0.9rem", color:"var(--saffron)", fontFamily:font.title, fontWeight:700,
                }}>{i + 1}</div>
                <span style={{ fontSize:"1rem", color:CK.muted, lineHeight:"1.6", paddingTop:"2px" }}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video setup */}
      {data.video_setup && (
        <div style={{ marginBottom:"28px" }}>
          <SectionTitle>Your Video Setup</SectionTitle>
          <div style={{
            background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
            borderRadius:"12px", padding:"16px 18px",
            fontSize:"1rem", color:CK.cream, lineHeight:"1.75",
          }}>
            <span style={{ marginRight:"8px" }}>📱</span>{data.video_setup}
          </div>
        </div>
      )}

      {/* Diksha teaser — actionable, not a price table */}
      {data.allows_diksha && (
        <div style={{
          background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
          borderRadius:"12px", padding:"16px 18px", marginBottom:"28px",
          display:"flex", alignItems:"center", gap:"14px",
        }}>
          <span style={{ fontSize:"1.6rem" }}>📿</span>
          <div>
            <div style={{ fontSize:"1rem", color:CK.glow, marginBottom:"3px", fontWeight:600 }}>
              Diksha Package Available
            </div>
            <div style={{ fontSize:"0.95rem", color:CK.dim, lineHeight:"1.6" }}>
              Commit to 4 sessions and save ~12%. Choose your group size in the next step — the Diksha option will appear there.
            </div>
          </div>
        </div>
      )}

      <Divider />

      {/* CTA */}
      <PrimaryBtn onClick={onBook}>
        Book This Puja →
      </PrimaryBtn>
      {!allItemsChecked && data.items_required && (
        <div style={{ fontSize:"0.95rem", color:CK.dim, textAlign:"center", marginTop:"10px" }}>
          Please arrange the items above before booking for the best experience.
        </div>
      )}
    </div>
  );
}

// ── Step 1: Browse ─────────────────────────────────────────────────────────────
function BrowseStep({ onSelect, profile, onEditProfile }) {
  const [category, setCategory] = useState("all");
  const [allPujas, setAllPujas] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // Fetch from API on mount
  useEffect(() => {
    const state = profile?.state || "";
    const url = state ? `${API}/puja/catalog?state=${state}` : `${API}/puja/catalog`;
    fetch(url)
      .then(r => r.json())
      .then(data => { setAllPujas(data); setLoadingCatalog(false); })
      .catch(() => { setAllPujas(PUJAS); setLoadingCatalog(false); }); // fallback to static
  }, [profile?.state]);

  const lang = profile?.language || "hindi";
  const langLabel = LANG_LABELS[lang] || "Hindi";

  // Categorise: regional (explicitly non-pan_india scope) and common (pan_india)
  // Guard: static fallback PUJAS have no scope field — treat them as pan_india
  const regionalPujas = allPujas.filter(p => p.scope && p.scope !== "pan_india");
  const commonPujas   = allPujas.filter(p => !p.scope || p.scope === "pan_india");
  const popularPujas  = allPujas.filter(p => p.popular);

  const getDisplayName = (p) => {
    const langCode = { hindi:"hi", telugu:"te", tamil:"ta", bengali:"bn", marathi:"mr" }[lang] || "en";
    if (p.names) return p.names[langCode] || p.names.en || p.name;
    return p.name;
  };

  const filteredAll = category === "all" ? allPujas : allPujas.filter(p => p.category === category);

  // Uses the module-level white CK palette.

  if (loadingCatalog) return (
    <div style={{ textAlign:"center", padding:"60px 0", color:CK.dim, background:CK.pageBg }}>
      <div style={{ fontSize:"2rem", marginBottom:"10px" }}>🪔</div>
      Loading pujas…
    </div>
  );

  return (
    <div style={{ background: CK.pageBg, width:"100%", padding:"0 0 40px", minHeight:"60vh" }}>
      <style>{SACRED_ANIM_CSS}</style>

      {/* ── Header ── */}
      <div style={{
        position:"relative", overflow:"hidden",
        margin:"0 0 32px",
        background: CK.headerBg,
        padding:"36px 48px 28px",
        textAlign:"center",
        borderBottom:`1px solid rgba(201,149,58,0.18)`,
      }}>
        {/* Amber glow from top — matches landing page feel */}
        <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)",
          width:340, height:180, pointerEvents:"none",
          background:"radial-gradient(ellipse at 50% 0%, rgba(201,149,58,0.16) 0%, rgba(180,100,15,0.07) 40%, transparent 70%)",
        }} />

        <div style={{ position:"relative", zIndex:2 }}>
          {/* OM — saffron glow on dark */}
          <div style={{
            fontFamily:font.sanskrit, fontSize:"3rem", color: CK.glow,
            filter:`drop-shadow(0 0 20px rgba(232,131,42,0.7)) drop-shadow(0 0 50px rgba(201,149,58,0.3))`,
            animation:"goldenPulse 4s ease-in-out infinite",
            lineHeight:1, marginBottom:12,
          }}>ॐ</div>
          <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.9rem", color: CK.cream,
            marginBottom:7, textShadow:"0 0 24px rgba(232,131,42,0.22)" }}>
            Online Puja Booking
          </div>
          <div style={{ fontSize:"1.1rem", color: CK.dim, marginBottom:16, lineHeight:1.7, fontWeight:500 }}>
            Live with a verified Vedic priest · Join from anywhere
          </div>
          {/* Separator — gold line */}
          <div style={{ height:1, background:`linear-gradient(90deg, transparent, rgba(201,149,58,0.5), rgba(232,131,42,0.8), rgba(201,149,58,0.5), transparent)`,
            margin:"0 auto 18px", maxWidth:260 }} />
          <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", justifyContent:"center", marginBottom:12 }}>
            <Pill color="var(--saffron)">🎥 Live Video</Pill>
            <Pill color="var(--saffron)">🕯️ Verified Priests</Pill>
            <Pill color="var(--saffron)">from ₹151/session</Pill>
          </div>
          {profile?.state && (
            <div style={{
              display:"inline-flex", alignItems:"center", gap:"8px",
              background:"rgba(232,131,42,0.1)", border:"1px solid rgba(232,131,42,0.32)",
              borderRadius:"20px", padding:"6px 14px", fontSize:"0.95rem",
            }}>
              <span style={{ color:"var(--saffron)" }}>📍 {INDIA_STATES.find(s => s.value === profile.state)?.label || profile.state}</span>
              <span style={{ color: CK.dim }}>·</span>
              <span style={{ color: CK.dim }}>{langLabel} tradition</span>
              <button onClick={onEditProfile} style={{
                background:"none", border:"none", color:"var(--saffron)", cursor:"pointer",
                fontSize:"0.95rem", padding:"0", fontFamily:font.body, textDecoration:"underline", fontWeight:600,
              }}>Change</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Regional pujas ── */}
      {profile?.state && (
        <div style={{ marginBottom:"32px", padding:"0 48px" }}>
          <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.25rem", color: CK.gold,
            textShadow:"0 0 20px rgba(232,131,42,0.18)",
            marginBottom:"14px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,${CK.kumkum},transparent)` }} />
            {langLabel} Tradition Pujas
            <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,transparent,${CK.kumkum})` }} />
          </div>
          {regionalPujas.length > 0 ? (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:"16px" }}>
              {regionalPujas.map(p => (
                <PujaCard key={p.id} puja={p} displayName={getDisplayName(p)} onClick={() => onSelect(p)} ck={CK} />
              ))}
            </div>
          ) : (
            <div style={{
              background: CK.cardBg, border:`1px solid ${CK.border}`,
              borderRadius:"14px", padding:"20px 22px",
              display:"flex", alignItems:"center", gap:"14px",
            }}>
              <span style={{ fontSize:"1.8rem" }}>🌿</span>
              <div>
                <div style={{ fontSize:"1rem", color: CK.cream, marginBottom:"4px", fontWeight:600 }}>
                  {langLabel} regional pujas coming soon
                </div>
                <div style={{ fontSize:"0.95rem", color: CK.dim, lineHeight:"1.6" }}>
                  We are working with priests from {INDIA_STATES.find(s => s.value === profile.state)?.label} to add region-specific pujas. All pan-India pujas below are available for you now.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Most Booked ── */}
      {popularPujas.length > 0 && category === "all" && (
        <div style={{ marginBottom:"32px", padding:"0 48px" }}>
          <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.25rem", color: CK.gold,
            textShadow:"0 0 20px rgba(232,131,42,0.18)",
            marginBottom:"14px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,${CK.kumkum},transparent)` }} />
            ⭐ Most Booked
            <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,transparent,${CK.kumkum})` }} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:"16px" }}>
            {popularPujas.filter(p => p.scope === "pan_india").map(p => (
              <PujaCard key={p.id} puja={p} displayName={getDisplayName(p)} onClick={() => onSelect(p)} ck={CK} />
            ))}
          </div>
        </div>
      )}

      {/* ── All Pujas with category filter ── */}
      <div style={{ padding:"0 48px" }}>
        <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.05rem", color: CK.gold,
          marginBottom:"14px", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,${CK.kumkum},transparent)` }} />
          All Pujas
          <span style={{ display:"inline-block", width:28, height:1, background:`linear-gradient(90deg,transparent,${CK.kumkum})` }} />
        </div>
        {/* Category filter chips */}
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"18px" }}>
          {CATEGORIES.map(c => {
            const active = category === c.id;
            return (
              <button key={c.id} onClick={() => setCategory(c.id)} style={{
                padding:"7px 16px", borderRadius:"24px",
                border:`1px solid ${active ? "var(--saffron)" : CK.border}`,
                background: active ? "rgba(232,131,42,0.14)" : "transparent",
                color: active ? CK.glow : CK.dim,
                fontSize:"0.95rem", cursor:"pointer", fontFamily:font.body,
                transition:"all 0.2s",
                fontWeight: active ? "600" : "400",
              }}>{c.icon} {c.label}</button>
            );
          })}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:"16px" }}>
          {filteredAll.map(p => (
            <PujaCard key={p.id} puja={p} displayName={getDisplayName(p)} onClick={() => onSelect(p)} ck={CK} />
          ))}
        </div>
      </div>
    </div>
  );
}

// updated BrowseStep ends — old one below removed
// ── Step 1: Browse (original — now replaced above) ────────────────────────────
function _BrowseStep_DEPRECATED({ onSelect }) {
  const [category, setCategory] = useState("all");
  const filtered = category === "all" ? PUJAS : PUJAS.filter(p => p.category === category);

  return (
    <div>
      {/* Hero */}
      <div style={{ marginBottom:"28px" }}>
        <div style={{ fontFamily:font.title, fontSize:"1.6rem", color:C.saffron, marginBottom:"6px" }}>
          Online Puja Booking
        </div>
        <div style={{ fontSize:"1rem", color:C.dim, fontStyle:"italic", marginBottom:"14px" }}>
          Live puja with a qualified priest · Join from anywhere
        </div>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap" }}>
          <Pill color="#22c55e">🎥 Live 45-min Video</Pill>
          <Pill color={C.saffron}>🕯️ Verified Priests</Pill>
          <Pill color="#6366f1">📿 Vedic Rituals</Pill>
          <Pill color="#14b8a6">from ₹89/session</Pill>
        </div>
      </div>

      {/* Category chips */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"24px" }}>
        {CATEGORIES.map(c => {
          const active = category === c.id;
          return (
            <button key={c.id} onClick={() => setCategory(c.id)} style={{
              padding:"8px 16px", borderRadius:"24px",
              border:`1px solid ${active ? C.saffron : C.border}`,
              background: active ? "rgba(232,131,42,0.12)" : "transparent",
              color: active ? C.saffron : C.dim,
              fontSize:"0.88rem", cursor:"pointer", fontFamily:font.body,
              transition:"all 0.2s",
            }}>{c.icon} {c.label}</button>
          );
        })}
      </div>

      {/* Puja grid — clicking a card goes directly to booking */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:"16px" }}>
        {filtered.map(puja => (
          <PujaCard key={puja.id} puja={puja} onClick={() => onSelect(puja)} />
        ))}
      </div>
    </div>
  );
}

function PujaCard({ puja, onClick, displayName, ck }) {
  const [hov, setHov] = useState(false);

  // Lowest community price
  const prices = puja.prices || {};
  const minPrice = prices.satsang || prices.mandali || prices.yugal || prices.ekanta || 89;
  const isOccasion = puja.session_types && puja.session_types.length === 1 && puja.session_types[0] === "ekanta";
  const deityLabel = puja.deity
    ? (typeof puja.deity === "object" ? (puja.deity.en || "") : puja.deity)
    : "";
  const benefit = puja.benefits?.[0] || puja.benefit || "";
  const mantra = puja.sanskrit_mantra || puja.sanskrit || "";
  const name = displayName || puja.name;

  // Use kumkum palette if passed, else default dark palette
  const cardBg    = ck ? ck.cardBg    : C.bg2;
  const textMain  = ck ? ck.cream     : C.cream;
  const textDim   = ck ? ck.dim       : C.dim;
  const textBody  = ck ? ck.muted     : C.text;
  const priceClr  = ck ? ck.glow      : C.saffron;
  const badgeClr  = ck ? ck.glow      : C.saffron;
  const borderBase = ck ? "var(--border)" : puja.color+"33";
  const borderHov  = ck ? "var(--saffron)" : puja.color+"aa";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: ck ? ck.cardBg : C.bg2,
        border:`1px solid ${hov ? borderHov : borderBase}`,
        borderRadius:"18px", padding:"22px 20px", cursor:"pointer",
        transition:"all 0.22s", position:"relative", overflow:"hidden",
        boxShadow: hov
          ? ck ? "var(--card-shadow-hover)" : `0 4px 24px ${puja.color}28`
          : ck ? "var(--card-shadow)" : "0 2px 8px rgba(0,0,0,0.3)",
        transform: hov ? "translateY(-3px)" : "none",
      }}>

      {/* Subtle top-edge glow on hover */}
      {ck && hov && (
        <div style={{
          position:"absolute", top:0, left:0, right:0, height:2,
          background:`linear-gradient(90deg, transparent, rgba(232,131,42,0.8), transparent)`,
          borderRadius:"18px 18px 0 0",
        }} />
      )}

      {/* Badges */}
      <div style={{ position:"absolute", top:"12px", right:"12px",
        display:"flex", flexDirection:"column", gap:"5px", alignItems:"flex-end" }}>
        {puja.popular && (
          <span style={{
            background: ck ? "rgba(232,131,42,0.14)" : "rgba(232,131,42,0.15)",
            border: ck ? "1px solid rgba(232,131,42,0.4)" : "1px solid rgba(232,131,42,0.4)",
            borderRadius:"12px", padding:"3px 9px", fontSize:"0.9rem", fontWeight:600,
            color: badgeClr,
          }}>★ Popular</span>
        )}
        {isOccasion && (
          <span style={{ background:"rgba(34,197,94,0.12)", border:"1px solid rgba(34,197,94,0.35)",
            borderRadius:"12px", padding:"3px 9px", fontSize:"0.9rem", fontWeight:600,
            color:"#15803d" }}>Private</span>
        )}
        {puja.allows_diksha && (
          <span style={{ background:"rgba(176,48,0,0.10)", border:`1px solid ${CK.borderHot}`,
            borderRadius:"12px", padding:"3px 9px", fontSize:"0.9rem", fontWeight:600,
            color:CK.kumkum }}>Diksha</span>
        )}
      </div>

      {/* Deity emoji — slightly larger for warmth */}
      <div style={{ fontSize:"2.4rem", marginBottom:"12px", lineHeight:1 }}>{puja.emoji}</div>

      {/* Puja name */}
      <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.2rem", color: textMain, marginBottom:"6px", lineHeight:1.3 }}>
        {name}
      </div>
      {/* Deity */}
      <div style={{ fontSize:"1.02rem", color: textDim, marginBottom:"9px" }}>{deityLabel}</div>
      {/* Benefit */}
      <div style={{ fontSize:"1.08rem", color: textBody, lineHeight:"1.6", marginBottom:"11px" }}>
        {benefit}
      </div>
      {/* Sanskrit mantra */}
      <div style={{ fontFamily:font.sanskrit, fontSize:"1.02rem", color: textDim,
        marginBottom:"12px" }}>{mantra}</div>

      {/* Day tags */}
      {puja.tags && (
        <div style={{ display:"flex", gap:"5px", flexWrap:"wrap", marginBottom:"12px" }}>
          {puja.tags.map(t => <Pill key={t} color={ck ? CK_TAG_COLOR(puja.color) : puja.color}>{t}</Pill>)}
        </div>
      )}

      {/* Footer: duration + price */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        borderTop: ck ? `1px solid var(--border)` : `1px solid ${puja.color}22`,
        paddingTop:"10px" }}>
        <span style={{ fontSize:"1.02rem", color: textDim }}>⏱ {puja.duration_mins || 45} min</span>
        <span style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.08rem", color: priceClr }}>
          {isOccasion ? `₹${minPrice.toLocaleString("en-IN")} →` : `from ₹${minPrice.toLocaleString("en-IN")} →`}
        </span>
      </div>
    </div>
  );
}

// Helper — darkens puja's own accent color so it stays readable as text on white
const PUJA_COLOR_DARK = {
  "#14b8a6": "#0f766e",
  "#22c55e": "#15803d",
  "#6366f1": "#4338ca",
  "#64748b": "#475569",
  "#8b5cf6": "#6d28d9",
  "#a855f7": "#7e22ce",
  "#ec4899": "#be185d",
  "#ef4444": "#dc2626",
  "#f59e0b": "#a16207",
  "#f97316": "#c2410c",
};
function CK_TAG_COLOR(pujaColor) {
  return PUJA_COLOR_DARK[pujaColor] || pujaColor || "#a16207";
}

// ── Step 2: Individual or Family ───────────────────────────────────────────────
function BookingTypeStep({ puja, onBack, onSelect }) {
  const [hov, setHov] = useState(null);

  const types = [
    {
      key: "family",
      icon: "🪔",
      title: "Individual or Family",
      subtitle: "One booking — for you or your whole family",
      desc: "One booking covers you alone or your entire household. All family members join on the same link. The priest reads your name and sankalpa aloud in the ritual.",
      points: ["You or your whole family — one link", "Priest reads every family member's name", "Your sankalpa offered to the deity"],
      color: "#f59e0b",
    },
    {
      key: "couple",
      icon: "💑",
      title: "Newly Married",
      subtitle: "Yugal Jodi — new couple",
      desc: "First pujas together as husband and wife carry special significance. The priest performs a special couple's sankalpa and addresses you both by name.",
      points: ["Couple sankalpa — both names, both gotras", "Milestone blessing for new life together", "Available for private Ekanta sessions"],
      color: "#ec4899",
    },
  ];

  return (
    <div style={{ background:CK.pageBg, minHeight:"60vh" }}>
      <BackBtn onClick={onBack} />

      {/* Puja header */}
      <div style={{
        display:"flex", alignItems:"center", gap:"14px", marginBottom:"24px",
        background:CK.cardBg,
        border:"1px solid var(--border)", borderRadius:"14px", padding:"16px 18px",
      }}>
        <span style={{ fontSize:"2.4rem" }}>{puja.emoji}</span>
        <div>
          <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.1rem", color:CK.cream, marginBottom:"3px" }}>
            {puja.name}
          </div>
          <div style={{ fontSize:"0.95rem", color:CK.dim }}>
            {typeof puja.deity === "object" ? (puja.deity?.en || "") : (puja.deity || "")}
            {" · "}{puja.duration_mins || 45} min · Live video
          </div>
          <div style={{ fontSize:"0.95rem", color:CK.muted, marginTop:"4px" }}>
            {puja.benefits?.[0] || puja.benefit || ""}
          </div>
        </div>
      </div>

      {/* Streamed puja: setup guide */}
      {!puja.home_ok && puja.home_setup && (
        <div style={{ background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
          borderRadius:"14px", padding:"18px 20px", marginBottom:"24px" }}>
          <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"0.98rem", color:CK.glow,
            marginBottom:"10px" }}>
            🎥 How this puja works
          </div>
          <div style={{ fontSize:"1rem", color:CK.muted, lineHeight:"1.7", marginBottom:"14px" }}>
            {puja.home_setup.note}
          </div>

          <div style={{ fontSize:"0.95rem", color:CK.dim, fontWeight:600, marginBottom:"8px" }}>
            Keep these items ready at home
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"5px", marginBottom:"14px" }}>
            {puja.home_setup.items.map((item, i) => (
              <div key={i} style={{ fontSize:"1rem", color:CK.muted, display:"flex", gap:"8px" }}>
                <span style={{ color:CK.glow }}>•</span> {item}
              </div>
            ))}
          </div>

          <div style={{ fontSize:"0.95rem", color:CK.dim, fontWeight:600, marginBottom:"8px" }}>
            Your participation during puja
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
            {puja.home_setup.actions.map((action, i) => (
              <div key={i} style={{ fontSize:"1rem", color:CK.muted, display:"flex", gap:"8px" }}>
                <span style={{ color:"#15803d" }}>✓</span> {action}
              </div>
            ))}
          </div>
        </div>
      )}

      <SectionTitle>Who is this booking for?</SectionTitle>

      <div className="puja-col-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px", marginBottom:"14px" }}>
        {types.slice(0, 2).map(t => (
          <div key={t.key}
            onClick={() => onSelect(t.key)}
            onMouseEnter={() => setHov(t.key)}
            onMouseLeave={() => setHov(null)}
            style={{
              background:CK.cardBg,
              border:`1px solid ${hov === t.key ? t.color : "var(--border)"}`,
              borderRadius:"16px", padding:"22px 18px", cursor:"pointer",
              transition:"all 0.2s",
              boxShadow: hov === t.key ? "var(--card-shadow-hover)" : "var(--card-shadow)",
              transform: hov === t.key ? "translateY(-2px)" : "none",
            }}>
            <div style={{ fontSize:"2.4rem", marginBottom:"10px" }}>{t.icon}</div>
            <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.25rem", color:CK.cream, marginBottom:"4px" }}>
              {t.title}
            </div>
            <div style={{ fontFamily:font.body, fontSize:"1.02rem", color:CK_TAG_COLOR(t.color), fontWeight:600, marginBottom:"10px" }}>{t.subtitle}</div>
            <div style={{ fontFamily:font.body, fontSize:"1.1rem", color:CK.muted, lineHeight:"1.65", marginBottom:"14px" }}>
              {t.desc}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
              {t.points.map((p, i) => (
                <div key={i} style={{ fontFamily:font.body, fontSize:"1.04rem", color:CK.muted, display:"flex", gap:"6px" }}>
                  <span style={{ color:CK_TAG_COLOR(t.color) }}>✓</span>{p}
                </div>
              ))}
            </div>
            <div style={{ marginTop:"14px", textAlign:"center",
              fontSize:"0.95rem", color:CK_TAG_COLOR(t.color), fontFamily:font.title, fontWeight:700 }}>
              Select →
            </div>
          </div>
        ))}
      </div>
      {/* Couple option — full width */}
      {types.slice(2).map(t => (
        <div key={t.key}
          onClick={() => onSelect(t.key)}
          onMouseEnter={() => setHov(t.key)}
          onMouseLeave={() => setHov(null)}
          style={{
            display:"flex", alignItems:"flex-start", gap:"16px",
            background:`linear-gradient(145deg, ${CK.cardBg}, ${t.color}0e)`,
            border:`1px solid ${hov === t.key ? t.color : t.color+"33"}`,
            borderRadius:"16px", padding:"18px 20px", cursor:"pointer",
            transition:"all 0.2s", marginBottom:"14px",
            boxShadow: hov === t.key ? "var(--card-shadow-hover)" : "var(--card-shadow)",
          }}>
          <div style={{ fontSize:"2.2rem", flexShrink:0 }}>{t.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.1rem", color:CK.cream, marginBottom:"3px" }}>
              {t.title}
            </div>
            <div style={{ fontSize:"0.95rem", color:CK_TAG_COLOR(t.color), fontWeight:600, marginBottom:"8px" }}>{t.subtitle}</div>
            <div style={{ fontSize:"1rem", color:CK.dim, lineHeight:"1.5", marginBottom:"10px" }}>
              {t.desc}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
              {t.points.map((p, i) => (
                <div key={i} style={{ fontSize:"0.95rem", color:CK.muted, display:"flex", gap:"5px" }}>
                  <span style={{ color:CK_TAG_COLOR(t.color) }}>✓</span>{p}
                </div>
              ))}
            </div>
          </div>
          <div style={{ fontSize:"0.95rem", color:CK_TAG_COLOR(t.color), fontFamily:font.title, fontWeight:700,
            flexShrink:0, paddingTop:"4px" }}>
            Select →
          </div>
        </div>
      ))}
      <div style={{ fontSize:"0.95rem", color:CK.dim, textAlign:"center" }}>
        Couple pricing applies to private Ekanta sessions only
      </div>
    </div>
  );
}

// ── Step 3: Choose Session Tier ────────────────────────────────────────────────
function TierStep({ puja, bookingType, onBack, onSelect }) {
  const [chosen,      setChosen]      = useState(null);
  const [dikshaMode,  setDikshaMode]  = useState(false);  // toggle: single vs diksha
  const isFamily = bookingType === "family";
  const isCouple = bookingType === "couple";

  // Diksha only available for mandali and ekanta (not satsang — slot fill risk)
  const DIKSHA_ELIGIBLE_TIERS = ["mandali", "yugal", "ekanta"];
  const chosenEligibleForDiksha = chosen && puja?.allows_diksha && DIKSHA_ELIGIBLE_TIERS.includes(chosen.key);

  function _auspiciousDiscount(amount, rate) {
    const discounted = amount * (1 - rate);
    const rounded = Math.round(discounted / 50) * 50;
    return Math.floor(rounded / 10) * 10 + 1;
  }

  const singlePrice  = chosen ? getPujaPrice(puja, chosen.key, bookingType) : null;
  const dikshaPrice  = chosenEligibleForDiksha && singlePrice
    ? _auspiciousDiscount(singlePrice * 4, 0.12)
    : null;
  const dakshinaSaving = singlePrice && dikshaPrice ? (singlePrice * 4) - dikshaPrice : 0;

  // Only show tiers allowed for this puja
  const availableTiers = getAvailableTiers(puja);
  const isOccasionOnly = availableTiers.length === 1 && availableTiers[0].key === "ekanta";

  return (
    <div style={{ background:CK.pageBg, minHeight:"60vh" }}>
      <BackBtn onClick={onBack} />
      <SectionTitle>Choose Your Experience</SectionTitle>
      <div style={{ fontSize:"0.98rem", color:CK.dim, marginBottom:"6px" }}>
        {puja?.duration_mins || 45} min · Live video with priest
      </div>
      {isOccasionOnly && (
        <div style={{
          background:"rgba(139,92,246,0.08)", border:"1px solid rgba(139,92,246,0.3)",
          borderRadius:"10px", padding:"10px 14px", marginBottom:"16px",
          fontSize:"0.95rem", color:"#6d28d9",
        }}>
          This is a personal life event — available as a private family session only.
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:"12px", marginBottom:"24px" }}>
        {availableTiers.map(t => {
          const price = getPujaPrice(puja, t.key, bookingType);
          if (!price) return null;
          const sel = chosen?.key === t.key;
          return (
            <div key={t.key} onClick={() => setChosen({ ...t, price })} style={{
              background: CK.cardBg,
              border:`1px solid ${sel ? t.color : CK.border}`,
              borderRadius:"14px", padding:"18px 20px", cursor:"pointer",
              transition:"all 0.2s",
              boxShadow: sel ? "var(--card-shadow-hover)" : "var(--card-shadow)",
            }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:"16px" }}>

                {/* Price column */}
                <div style={{ textAlign:"center", minWidth:"64px", paddingTop:"2px" }}>
                  <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.5rem",
                    color: sel ? CK_TAG_COLOR(t.color) : CK.glow }}>₹{price.toLocaleString("en-IN")}</div>
                  <div style={{ fontSize:"0.95rem", color:CK.dim, marginTop:"2px" }}>
                    {isCouple ? "per couple" : isFamily ? "per family" : "per person"}
                  </div>
                </div>

                {/* Details */}
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                    <span style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.2rem", color:CK.cream }}>
                      {t.label}
                    </span>
                    {t.badge && (
                      <span style={{ background:`${t.color}18`, border:`1px solid ${t.color}44`,
                        borderRadius:"12px", padding:"3px 9px", fontSize:"0.9rem", fontWeight:600,
                        color:CK_TAG_COLOR(t.color) }}>{t.badge}</span>
                    )}
                  </div>
                  {/* Slot count — prominent */}
                  <div style={{
                    display:"inline-flex", alignItems:"center", gap:"6px",
                    background:`${t.color}18`, border:`1px solid ${t.color}44`,
                    borderRadius:"20px", padding:"3px 10px", marginBottom:"8px",
                    fontSize:"0.95rem", color:CK_TAG_COLOR(t.color), fontFamily:font.title, fontWeight:600,
                  }}>
                    {isCouple ? "💑" : isFamily ? "🏠" : "👥"} {isCouple ? t.family_slot_label : isFamily ? t.family_slot_label : t.slot_label}
                  </div>
                  {/* Tagline — short quoted line, italic ok */}
                  <div style={{ fontFamily:font.body, fontSize:"1rem", color: sel ? CK.cream : CK.muted,
                    fontStyle:"italic", marginBottom:"8px", lineHeight:"1.4" }}>
                    "{t.tagline}"
                  </div>
                  <div style={{ fontFamily:font.body, fontSize:"1.1rem", color: CK.muted,
                    lineHeight:"1.65", marginBottom:"10px" }}>
                    {t.experience}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
                    {t.includes.map((inc, i) => (
                      <div key={i} style={{ fontFamily:font.body, fontSize:"1.04rem",
                        color: CK.muted, display:"flex", gap:"6px" }}>
                        <span style={{ color:CK_TAG_COLOR(t.color) }}>✓</span>{inc}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Radio */}
                <div style={{
                  width:"20px", height:"20px", borderRadius:"50%", flexShrink:0,
                  border:`2px solid ${sel ? t.color : CK.border}`,
                  background: sel ? t.color : "transparent",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  marginTop:"2px",
                }}>
                  {sel && <span style={{ color:"white", fontSize:"0.7rem" }}>✓</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* <div style={{ background:C.bg2, border:`1px solid ${C.border}`,
        borderRadius:"12px", padding:"14px 16px", marginBottom:"20px" }}>
        <div style={{ fontSize:"0.7rem", color:C.dim, letterSpacing:"0.07em",
          textTransform:"uppercase", marginBottom:"6px" }}>Profit breakdown (per session)</div>
      </div> */}

      {/* Diksha upsell — only for mandali/yugal/ekanta (not satsang) */}
      {chosenEligibleForDiksha && dikshaPrice && (
        <div style={{
          background:CK.cardBg, boxShadow:"var(--card-shadow)",
          border:`1px solid ${dikshaMode ? CK.kumkum : CK.border}`,
          borderRadius:"14px", padding:"16px 18px", marginBottom:"20px",
          transition:"border-color 0.2s",
        }}>
          <div style={{ fontSize:"1rem", color:CK.glow, marginBottom:"10px", fontWeight:700 }}>
            📿 Diksha package available
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
            <div>
              <div style={{ fontSize:"1rem", color:CK.cream, marginBottom:"3px" }}>
                Commit to 4 sessions, save ₹{dakshinaSaving.toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize:"0.95rem", color:CK.dim }}>
                4 × ₹{singlePrice.toLocaleString("en-IN")} = ₹{(singlePrice * 4).toLocaleString("en-IN")} → package ₹{dikshaPrice.toLocaleString("en-IN")}
              </div>
            </div>
            <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.15rem", color:CK.kumkum, marginLeft:"12px" }}>
              ₹{dikshaPrice.toLocaleString("en-IN")}
            </div>
          </div>
          <div style={{ display:"flex", gap:"8px" }}>
            <button onClick={() => setDikshaMode(false)} style={{
              flex:1, padding:"9px", borderRadius:"8px", cursor:"pointer",
              fontFamily:font.body, fontSize:"0.95rem",
              background: !dikshaMode ? "rgba(232,131,42,0.12)" : CK.cardBg2,
              border:`1px solid ${!dikshaMode ? CK.glow : CK.border}`,
              color: !dikshaMode ? CK.glow : CK.dim, transition:"all 0.15s",
            }}>
              Single session<br/>
              <span style={{ fontSize:"0.92rem" }}>₹{singlePrice.toLocaleString("en-IN")}</span>
            </button>
            <button onClick={() => setDikshaMode(true)} style={{
              flex:1, padding:"9px", borderRadius:"8px", cursor:"pointer",
              fontFamily:font.body, fontSize:"0.95rem",
              background: dikshaMode ? "rgba(176,48,0,0.12)" : "transparent",
              border:`1px solid ${dikshaMode ? CK.kumkum : CK.border}`,
              color: dikshaMode ? CK.kumkum : CK.dim, transition:"all 0.15s",
            }}>
              4-session Diksha<br/>
              <span style={{ fontSize:"0.92rem" }}>₹{dikshaPrice.toLocaleString("en-IN")} total</span>
            </button>
          </div>
        </div>
      )}

      <PrimaryBtn
        disabled={!chosen}
        onClick={() => {
          if (!chosen) return;
          const price = (dikshaMode && dikshaPrice) ? dikshaPrice : singlePrice;
          onSelect({
            ...chosen,
            price,
            isDiksha:    dikshaMode && !!dikshaPrice,
            sessions:    dikshaMode && dikshaPrice ? 4 : 1,
            dikshaPrice: dikshaMode ? dikshaPrice : null,
          });
        }}
      >
        {dikshaMode && dikshaPrice
          ? `Book 4-session ${chosen?.label} Diksha — ₹${dikshaPrice?.toLocaleString("en-IN")} →`
          : `Continue with ${chosen?.label || "selected"} →`
        }
      </PrimaryBtn>
    </div>
  );
}

// ── Step 4: Booking Form ───────────────────────────────────────────────────────
function BookingFormStep({ puja, bookingType, tier, onBack, onConfirm }) {
  const isFamily = bookingType === "family";
  const isCouple = bookingType === "couple";
  const samagriCost = getSamagriCost(puja, tier.key);
  const [name,     setName]     = useState("");
  const [date,     setDate]     = useState("");
  const [time,     setTime]     = useState("");
  const [members,  setMembers]  = useState("1");
  const [gotra,    setGotra]    = useState("");
  const [occasion, setOccasion] = useState("");
  const [sankalpa, setSankalpa] = useState("");

  const slots = [];
  for (let h = 6; h <= 20; h++) slots.push(`${String(h).padStart(2,"0")}:00`);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  const canSubmit = name.trim() && date;

  return (
    <div>
      <BackBtn onClick={onBack} />

      {/* Summary bar */}
      <div style={{
        background:CK.cardBg,
        border:"1px solid var(--border)", borderRadius:"12px", boxShadow:"var(--card-shadow)",
        padding:"14px 18px", marginBottom:"20px",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", flexWrap:"wrap" }}>
          <span style={{ fontSize:"1.9rem" }}>{puja.emoji}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.05rem", color:CK.cream }}>{puja.name}</div>
            <div style={{ fontSize:"0.95rem", color:CK.dim, marginTop:"2px" }}>
              {tier.label} · {(isFamily || isCouple) ? tier.family_slot_label : tier.slot_label} · {puja.duration_mins || 45} min
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.2rem", color:CK.glow }}>
              ₹{tier.price?.toLocaleString("en-IN")}
            </div>
            {tier.isDiksha && (
              <div style={{ fontSize:"0.9rem", color:"#15803d" }}>
                4-session total
              </div>
            )}
          </div>
        </div>
        {/* Diksha banner */}
        {tier.isDiksha && (
          <div style={{
            marginTop:"10px", borderTop:`1px solid ${puja.color}22`, paddingTop:"10px",
            display:"flex", alignItems:"center", gap:"8px",
          }}>
            <span style={{ fontSize:"1rem" }}>📿</span>
            <div>
              <span style={{ fontSize:"1rem", color:CK.glow, fontWeight:600 }}>Diksha Package — 4 sessions</span>
              <span style={{ fontSize:"0.95rem", color:CK.dim, marginLeft:"8px" }}>
                ₹{(tier.price / 4).toFixed(0)} per session effectively
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
        <div style={{ gridColumn:"1/-1" }}>
          <FieldLabel>{isCouple ? "Husband's name *" : isFamily ? "Family head name *" : "Your name *"}</FieldLabel>
          <input style={{ ...inputStyle, marginBottom:"14px" }} value={name}
            onChange={e => setName(e.target.value)}
            placeholder={isCouple ? "How should the priest address you both?" : isFamily ? "How should the priest address your family?" : "As the priest should address you"} />
        </div>

        <div>
          <FieldLabel>Date *</FieldLabel>
          <input type="date" style={{ ...inputStyle, marginBottom:"14px" }} value={date}
            min={minDate} onChange={e => setDate(e.target.value)} />
        </div>

        <div>
          <FieldLabel>Preferred time (optional)</FieldLabel>
          <select style={{ ...inputStyle, marginBottom:"14px", cursor:"pointer" }}
            value={time} onChange={e => setTime(e.target.value)}>
            <option value="">Select time</option>
            {slots.map(s => <option key={s} value={s}>{s} IST</option>)}
          </select>
        </div>

        {isFamily && !isCouple && (
          <div style={{ gridColumn:"1/-1" }}>
            <FieldLabel>Number of family members joining</FieldLabel>
            <select style={{ ...inputStyle, marginBottom:"14px", cursor:"pointer" }}
              value={members} onChange={e => setMembers(e.target.value)}>
              {[1,2,3,4,5,6,7,8,9,10,"10+"].map(n => (
                <option key={n} value={n}>{n} member{n !== 1 && n !== "10+" ? "s" : ""}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <FieldLabel>Gotra (optional)</FieldLabel>
          <input style={{ ...inputStyle, marginBottom:"14px" }} value={gotra}
            onChange={e => setGotra(e.target.value)} placeholder="e.g. Bharadwaja" />
        </div>

        <div>
          <FieldLabel>Occasion (optional)</FieldLabel>
          <input style={{ ...inputStyle, marginBottom:"14px" }} value={occasion}
            onChange={e => setOccasion(e.target.value)}
            placeholder="e.g. Birthday, new job..." />
        </div>

        <div style={{ gridColumn:"1/-1" }}>
          <FieldLabel>Sankalpa — what are you praying for? (optional)</FieldLabel>
          <textarea style={{ ...inputStyle, resize:"vertical", minHeight:"80px",
            lineHeight:"1.6", marginBottom:"14px" }}
            value={sankalpa} onChange={e => setSankalpa(e.target.value)}
            placeholder="e.g. Health of my father, peace in family, success in new business..." />
        </div>
      </div>

      {/* Time note — no slot anxiety */}
      <div style={{ background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
        borderRadius:"10px", padding:"12px 16px", marginBottom:"20px",
        fontSize:"1rem", color:CK.dim, lineHeight:"1.7" }}>
        📅 <strong style={{ color:CK.cream }}>We will confirm your session within 24 hours.</strong>{" "}
        Date is required. Time is a preference — we will match it as closely as possible and
        send the final confirmed time to your WhatsApp before the puja.
      </div>

      <Divider />
      <SectionTitle>Order Summary</SectionTitle>

      <div style={{ background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
        borderRadius:"12px", padding:"16px 18px", marginBottom:"20px" }}>
        {[
          ["Puja",         `${puja.emoji} ${puja.name}`],
          ["Session",      tier.label],
          ["Booking for",  isCouple ? "Newly Married Couple" : isFamily ? `Family (${members} members)` : "Individual"],
          ["Duration",     `${puja.duration_mins || 45} minutes`],
          ...(tier.isDiksha ? [["Package", "📿 Diksha — 4 sessions"]] : []),
          ...(date && time ? [["Scheduled", `${date} at ${time} IST`]] : []),
        ].map(([k, v]) => (
          <div key={k} style={{ display:"flex", justifyContent:"space-between",
            padding:"7px 0", borderBottom:`1px solid var(--border)`,
            fontSize:"1rem" }}>
            <span style={{ color:CK.dim }}>{k}</span>
            <span style={{ color:CK.muted }}>{v}</span>
          </div>
        ))}
        {/* Samagri — only for community tiers (group sharing makes it meaningful) */}
        {["satsang","mandali","yugal"].includes(tier.key) && (
          <div style={{ padding:"10px 0 6px", borderBottom:`1px solid ${CK.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.95rem", marginBottom:"5px" }}>
              <span style={{ color:CK.dim }}>Puja fee</span>
              <span style={{ color:CK.muted }}>₹{tier.price?.toLocaleString("en-IN")}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.95rem" }}>
              <span style={{ color:CK.dim }}>
                Group samagri share
                <span style={{ fontSize:"0.9rem", color:"#15803d", marginLeft:"4px" }}>
                  (shared with {tier.slots - 1} other families)
                </span>
              </span>
              <span style={{ color:CK.muted }}>₹{samagriCost}</span>
            </div>
          </div>
        )}
        <div style={{ display:"flex", justifyContent:"space-between",
          paddingTop:"12px", alignItems:"center" }}>
          <div>
            <span style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.05rem", color:CK.cream }}>Total</span>
            {tier.isDiksha && (
              <div style={{ fontSize:"0.9rem", color:CK.dim }}>
                covers all 4 sessions
              </div>
            )}
          </div>
          <div>
            <span style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.5rem", color:CK.glow }}>
              ₹{((tier.price || 0) + samagriCost).toLocaleString("en-IN")}
            </span>
            {samagriCost > 0 && (
              <div style={{ fontSize:"0.9rem", color:CK.dim, marginTop:"1px", textAlign:"right" }}>
                puja + samagri
              </div>
            )}
          </div>
        </div>
      </div>

      <PrimaryBtn disabled={!canSubmit}
        onClick={() => canSubmit && onConfirm({ puja, bookingType, tier, name, date, time, members, gotra, occasion, sankalpa, samagriCost })}>
        Proceed to Payment · ₹{((tier.price || 0) + samagriCost).toLocaleString("en-IN")}
      </PrimaryBtn>
      {!canSubmit && (
        <div style={{ textAlign:"center", fontSize:"0.95rem", color:CK.dim, marginTop:"8px" }}>
          Please fill name and date to continue
        </div>
      )}
    </div>
  );
}

// ── Step 5: Payment ────────────────────────────────────────────────────────────
function PaymentStep({ booking, onBack, onSuccess }) {
  const [sent,      setSent]      = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  const handleWhatsApp = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/puja/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id:        booking.user?.id || "anonymous",
          puja_id:        booking.puja.id,
          puja_name:      typeof booking.puja.name === "object" ? (booking.puja.name?.en || "") : (booking.puja.name || ""),
          booking_type:   booking.bookingType,
          tier:           booking.tier.key,
          price:          booking.tier.price,
          is_diksha:      booking.tier.isDiksha || false,
          total_sessions: booking.tier.sessions || 1,
          name:           booking.name,
          preferred_date: booking.date,
          preferred_time: booking.time,
          members:        booking.members || "1",
          gotra:          booking.gotra || "",
          occasion:       booking.occasion || "",
          sankalpa:       booking.sankalpa || "",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setBookingId(data.booking_id);
      }
    } catch (_) {
      setError("Could not save booking — but you can still send via WhatsApp.");
    } finally {
      setSaving(false);
    }
    const msg = encodeURIComponent(
      `🙏 Puja Booking — VedSanga\n\n` +
      `Puja: ${booking.puja.name}\n` +
      `For: ${booking.bookingType === "couple" ? "Newly Married Couple" : booking.bookingType === "family" ? `Family (${booking.members} members)` : "Individual"}\n` +
      `Session: ${booking.tier.label} — ₹${booking.tier.price} + ₹${booking.samagriCost || 0} samagri\n` +
      `Date: ${booking.date} at ${booking.time} IST\n` +
      `Name: ${booking.name}\n` +
      (booking.gotra    ? `Gotra: ${booking.gotra}\n`       : "") +
      (booking.occasion ? `Occasion: ${booking.occasion}\n` : "") +
      (booking.sankalpa ? `Sankalpa: ${booking.sankalpa}\n` : "") +
      `\nPlease confirm and share UPI QR. 🙏`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
    setSent(true);
  };



  return (
    <div>
      <BackBtn onClick={onBack} />
      <SectionTitle>Complete Payment</SectionTitle>

      <div style={{ background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
        borderRadius:"12px", padding:"18px 20px", marginBottom:"20px" }}>
        <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.1rem", color:CK.cream,
          marginBottom:"12px", paddingBottom:"12px", borderBottom:`1px solid ${CK.border}` }}>
          {booking.puja.emoji} {booking.puja.name}
        </div>
        {[
          ["Session",    booking.tier.label],
          ["For",        booking.bookingType === "couple" ? "Newly Married Couple" : booking.bookingType === "family" ? `Family (${booking.members} members)` : "Individual"],
          ["Scheduled",  `${booking.date} · ${booking.time} IST`],
        ].map(([k, v]) => (
          <div key={k} style={{ display:"flex", justifyContent:"space-between",
            padding:"7px 0", borderBottom:`1px solid ${CK.border}`, fontSize:"1rem" }}>
            <span style={{ color:CK.dim }}>{k}</span><span style={{ color:CK.muted }}>{v}</span>
          </div>
        ))}
        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:"12px", alignItems:"center" }}>
          <div>
            <span style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.05rem", color:CK.cream }}>Total</span>
            {booking.samagriCost > 0 && (
              <div style={{ fontSize:"0.9rem", color:CK.dim }}>puja + samagri</div>
            )}
          </div>
          <span style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.5rem", color:CK.glow }}>
            ₹{(booking.tier.price + (booking.samagriCost || 0)).toLocaleString("en-IN")}
          </span>
        </div>
      </div>

      <div style={{ background:CK.cardBg, border:`1px solid ${CK.border}`, boxShadow:"var(--card-shadow)",
        borderRadius:"12px", padding:"20px", marginBottom:"20px", textAlign:"center" }}>
        <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.05rem", color:CK.glow, marginBottom:"8px" }}>
          Payment via WhatsApp
        </div>
        <div style={{ fontSize:"1rem", color:CK.dim, lineHeight:"1.7", marginBottom:"16px" }}>
          Online payment gateway coming soon. Send your booking via WhatsApp and we will
          confirm your time and share a UPI QR to complete the payment.
        </div>
        <button onClick={handleWhatsApp} disabled={saving} style={{
          background: saving ? "#aaa" : "#25D366", border:"none", borderRadius:"10px",
          padding:"12px 28px", color:"white", fontFamily:font.body, fontWeight:600,
          fontSize:"1rem", cursor: saving ? "not-allowed" : "pointer",
          display:"inline-flex", alignItems:"center", gap:"8px",
        }}>
          {saving ? "Saving..." : "📲 Send Booking via WhatsApp"}
        </button>
        {error && <div style={{ fontSize:"0.95rem", color:"#dc2626", marginTop:"8px" }}>{error}</div>}
        {bookingId && (
          <div style={{ fontSize:"0.95rem", color:"#15803d", marginTop:"8px" }}>
            ✓ Booking saved · ID: {bookingId.slice(0,8)}…
          </div>
        )}
      </div>

      {sent && (
        <>
          <div style={{ textAlign:"center", fontSize:"1rem",
            color:CK.dim, marginBottom:"14px" }}>
            Sent? Click below after completing the UPI payment.
          </div>
          <PrimaryBtn onClick={onSuccess}>✓ Payment Done — Confirm Booking</PrimaryBtn>
        </>
      )}
    </div>
  );
}

// ── Step 6: Success ────────────────────────────────────────────────────────────
function SuccessStep({ booking, onDone }) {
  return (
    <div style={{
      background:`linear-gradient(145deg, ${CK.pageBg}, rgba(34,197,94,0.04))`,
      border:"1px solid rgba(34,197,94,0.3)", borderRadius:"20px",
      padding:"48px 32px", textAlign:"center",
    }}>
      <div style={{ fontSize:"3.8rem", marginBottom:"10px" }}>🙏</div>
      <div style={{ fontFamily:font.title, fontSize:"1.3rem", color:CK.cream, marginBottom:"8px" }}>
        Puja Booking Confirmed
      </div>
      <div style={{ fontFamily:font.sanskrit, fontSize:"1.5rem", color:CK.glow,
        marginBottom:"18px", filter:"drop-shadow(0 0 10px rgba(192,90,0,0.3))" }}>
        ॐ तत् सत्
      </div>
      <div style={{ fontSize:"1rem", color:CK.dim, lineHeight:"1.9",
        maxWidth:"400px", margin:"0 auto 20px" }}>
        <strong style={{ color:CK.cream }}>{booking.puja.name}</strong> is booked for{" "}
        <strong style={{ color:CK.cream }}>{booking.date} at {booking.time} IST</strong>.
      </div>
      <div style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)",
        borderRadius:"10px", padding:"14px 18px", marginBottom:"28px",
        fontSize:"0.95rem", color:"#15803d", lineHeight:"1.8",
        maxWidth:"400px", margin:"0 auto 28px" }}>
        ✓ Live video link will be sent 30 min before the puja<br />
        ✓ Priest will be ready at your chosen time<br />
        ✓ Your sankalpa will be read at the start of the puja
      </div>
      <PrimaryBtn onClick={onDone} style={{ maxWidth:"200px", margin:"0 auto" }}>
        Back to Pujas
      </PrimaryBtn>
    </div>
  );
}

// ── Status badge helper ────────────────────────────────────────────────────────
const STATUS_STYLE = {
  pending:   { bg: "rgba(234,179,8,0.12)",   border: "rgba(234,179,8,0.35)",   color: "#a16207", label: "⏳ Pending"   },
  confirmed: { bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.35)",   color: "#15803d", label: "✅ Confirmed" },
  completed: { bg: "rgba(176,48,0,0.08)",    border: "rgba(176,48,0,0.30)",    color: "#B03000",  label: "🙏 Completed" },
  cancelled: { bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.35)",   color: "#dc2626", label: "❌ Cancelled" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: "12px", padding: "3px 10px",
      fontSize: "0.9rem", fontWeight: 600,
    }}>{s.label}</span>
  );
}

// ── My Bookings tab ────────────────────────────────────────────────────────────
function MyBookingsTab({ user, onJoin }) {
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [joining,  setJoining]  = useState(null);
  const [err,      setErr]      = useState("");

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    fetch(`${API}/puja/bookings/${user.id}`)
      .then(r => r.json())
      .then(data => { setBookings(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [user]);

  // device: "1" = publisher (camera + mic), "monitor" = watch-only + controls
  const handleJoin = async (b, device = "1") => {
    if (!user?.id) return;
    setJoining(device === "1" ? b.id : `${b.id}_monitor`); setErr("");
    try {
      const res  = await fetch(`${API}/puja/token/${b.id}?user_id=${encodeURIComponent(user.id)}&device=${device}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to get token");
      onJoin(b, {
        appId:       data.app_id,
        token:       data.token,
        channel:     data.channel,
        uid:         data.uid,
        priestUid:   data.priest_uid,
        monitorUid:  data.monitor_uid,
        priestName:  data.priest_name,
        priestUpi:   data.priest_upi_id || "",
        deviceNum:   data.device_num || "1",  // "1" or "monitor"
        bookingName: data.booking_name || "",
      });
    } catch (e) {
      setErr(e.message || "Could not join. Try again.");
    } finally {
      setJoining(null);
    }
  };

  if (!user?.id) return (
    <div style={{ textAlign:"center", padding:"60px 20px", color:CK.dim, fontSize:"1rem" }}>
      Please sign in to view your bookings.
    </div>
  );

  if (loading) return (
    <div style={{ textAlign:"center", padding:"60px 20px", color:CK.dim }}>Loading your bookings…</div>
  );

  if (!bookings.length) return (
    <div style={{ textAlign:"center", padding:"60px 20px" }}>
      <div style={{ fontSize:"3rem", marginBottom:"12px" }}>🙏</div>
      <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.1rem", color:CK.cream, marginBottom:"8px" }}>
        No bookings yet
      </div>
      <div style={{ fontSize:"1rem", color:CK.dim }}>
        Book your first puja above to begin.
      </div>
    </div>
  );

  return (
    <div>
      <SectionTitle>My Puja Bookings</SectionTitle>
      {err && (
        <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
          borderRadius:"10px", padding:"10px 14px", marginBottom:"16px",
          fontSize:"0.95rem", color:"#dc2626" }}>
          {err}
        </div>
      )}
      <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
        {bookings.map(b => {
          const puja = PUJAS.find(p => p.id === b.puja_id);
          const isJoinable = b.status === "confirmed";
          return (
            <div key={b.id} style={{
              background: CK.cardBg, border:`1px solid ${CK.border}`,
              borderRadius:"14px", padding:"18px 20px",
              boxShadow:"var(--card-shadow)",
            }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
                flexWrap:"wrap", gap:"10px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:"12px", flex:1 }}>
                  <span style={{ fontSize:"2rem" }}>{puja?.emoji || "🪔"}</span>
                  <div>
                    <div style={{ fontFamily:font.title, fontWeight:700,
                      fontSize:"1.05rem", color:CK.cream, marginBottom:"3px" }}>
                      {b.puja_name}
                    </div>
                    <div style={{ fontSize:"0.95rem", color:CK.dim }}>
                      {b.tier.charAt(0).toUpperCase()+b.tier.slice(1)} ·{" "}
                      {b.booking_type === "couple" ? "Couple" : "Family"} · ₹{b.price}
                    </div>
                    <div style={{ fontSize:"0.95rem", color:CK.dim, marginTop:"2px" }}>
                      📅 {b.preferred_date}{b.preferred_time ? ` · ${b.preferred_time} IST` : ""}
                    </div>
                    {b.priest_name && (
                      <div style={{ fontSize:"0.95rem", color:CK.glow, marginTop:"2px" }}>
                        🔱 Priest: {b.priest_name}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"8px" }}>
                  <StatusBadge status={b.status} />
                  {isJoinable && (
                    <div style={{ display:"flex", flexDirection:"column", gap:"6px", alignItems:"flex-end" }}>
                      {/* Primary: publish camera + mic, full participation */}
                      <button
                        onClick={() => handleJoin(b, "1")}
                        disabled={!!joining}
                        style={{
                          background: joining === b.id
                            ? "rgba(232,131,42,0.3)"
                            : "var(--saffron)",
                          border:"none", borderRadius:"10px", padding:"9px 20px",
                          color:"white", fontFamily:font.title, fontWeight:700,
                          fontSize:"0.95rem",
                          cursor: joining ? "wait" : "pointer",
                          transition:"all 0.2s",
                          boxShadow: joining ? "none" : "0 4px 16px rgba(232,131,42,0.3)",
                        }}>
                        {joining === b.id ? "Connecting…" : "🎥 Join Puja"}
                      </button>
                      {/* Monitor: watch-only on a second device (phone/tablet/laptop) */}
                      <button
                        onClick={() => handleJoin(b, "monitor")}
                        disabled={!!joining}
                        title="Open this on a second phone, tablet, or laptop to watch on a bigger screen. Your camera stays on the first device."
                        style={{
                          background: "transparent",
                          border:`1px solid ${CK.border}`,
                          borderRadius:"10px", padding:"6px 14px",
                          color: CK.dim, fontSize:"0.9rem",
                          cursor: joining ? "wait" : "pointer",
                          transition:"all 0.2s", whiteSpace:"nowrap",
                        }}>
                        {joining === `${b.id}_monitor` ? "Connecting…" : "📺 Watch on 2nd device"}
                      </button>
                    </div>
                  )}
                  {b.status === "pending" && (
                    <div style={{ fontSize:"0.9rem", color:CK.dim, textAlign:"right" }}>
                      We confirm within 24 hrs
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize:"0.9rem", color:CK.dim, marginTop:"10px",
                paddingTop:"10px", borderTop:`1px solid ${CK.border}` }}>
                Booking ID: {b.id.slice(0,8)}…
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Top tab bar ────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  return (
    <div style={{
      display:"flex", gap:"4px", marginBottom:"28px",
      background:CK.cardBg, borderRadius:"12px", padding:"4px",
      border:`1px solid ${CK.border}`, width:"fit-content",
    }}>
      {[
        ["browse",     "🪔 Book a Puja"],
        ["myBookings", "📋 My Bookings"],
      ].map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)} style={{
          padding:"8px 20px", borderRadius:"8px", border:"none",
          background: active === v ? "rgba(232,131,42,0.12)" : "transparent",
          color:      active === v ? CK.glow : CK.dim,
          fontFamily:font.body, fontWeight: active === v ? 700 : 400, fontSize:"0.98rem",
          cursor:"pointer", transition:"all 0.2s",
          borderBottom: active === v ? `2px solid ${CK.glow}` : "2px solid transparent",
        }}>{label}</button>
      ))}
    </div>
  );
}

// ── Role selector ─────────────────────────────────────────────────────────────
function RoleSelector({ onSelect }) {
  return (
    <div style={{ padding:"32px", maxWidth:"700px", background:CK.pageBg, minHeight:"60vh" }}>
      <div style={{ marginBottom:"32px" }}>
        <div style={{ fontFamily:font.title, fontWeight:700, fontSize:"1.6rem", color:CK.glow, marginBottom:"6px" }}>
          Online Puja
        </div>
        <div style={{ fontFamily:font.body, fontSize:"1.08rem", color:CK.muted, lineHeight:"1.6" }}>
          Live puja sessions with verified priests — are you here to pray or to conduct?
        </div>
      </div>
      <div className="puja-col-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"20px" }}>
        {/* Devotee card */}
        <button onClick={() => onSelect("devotee")} style={{
          background: CK.cardBg, border:`1px solid ${CK.border}`,
          borderRadius:"18px", padding:"20px 18px",
          cursor:"pointer", textAlign:"left", fontFamily:font.body,
          transition:"all 0.2s", outline:"none",
          boxShadow:"var(--card-shadow)",
        }}
          onMouseOver={e => { e.currentTarget.style.borderColor = CK.glow; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = CK.border; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
        >
          <div style={{ fontSize:"2.8rem", marginBottom:"16px" }}>🙏</div>
          <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.2rem", color:CK.cream, marginBottom:"10px" }}>
            I'm a Devotee
          </div>
          <div style={{ fontFamily:font.body, fontSize:"1.1rem", color:CK.muted, lineHeight:"1.7" }}>
            Book a live puja session, choose your deity and tier, attend with a priest who reads your name and sankalpa.
          </div>
          <div style={{
            marginTop:"20px", display:"inline-flex", alignItems:"center", gap:"6px",
            color:CK.glow, fontSize:"0.95rem", fontWeight:600,
          }}>
            Book a Puja →
          </div>
        </button>

        {/* Priest card */}
        <button onClick={() => onSelect("priest")} style={{
          background: CK.cardBg, border:`1px solid ${CK.border}`,
          borderRadius:"18px", padding:"20px 18px",
          cursor:"pointer", textAlign:"left", fontFamily:font.body,
          transition:"all 0.2s", outline:"none",
          boxShadow:"var(--card-shadow)",
        }}
          onMouseOver={e => { e.currentTarget.style.borderColor = CK.glow; e.currentTarget.style.boxShadow = "var(--card-shadow-hover)"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = CK.border; e.currentTarget.style.boxShadow = "var(--card-shadow)"; }}
        >
          <div style={{ fontSize:"2.8rem", marginBottom:"16px" }}>🔱</div>
          <div style={{ fontFamily:font.title, fontWeight:800, fontSize:"1.2rem", color:CK.cream, marginBottom:"10px" }}>
            I'm a Priest
          </div>
          <div style={{ fontFamily:font.body, fontSize:"1.1rem", color:CK.muted, lineHeight:"1.7" }}>
            Register to conduct pujas, manage your bookings, view your schedule, and join live sessions directly from here.
          </div>
          <div style={{
            marginTop:"20px", display:"inline-flex", alignItems:"center", gap:"6px",
            color:CK.glow, fontSize:"0.95rem", fontWeight:600,
          }}>
            Priest Portal →
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function PujaPage({ user, onLoginRequired }) {
  const [role,           setRole]          = useState(null);
  const [view,           setView]          = useState("browse");
  const [step,           setStep]          = useState("browse");
  const [puja,           setPuja]          = useState(null);
  const [bookingType,    setBookingType]   = useState(null);
  const [tier,           setTier]          = useState(null);
  const [booking,        setBooking]       = useState(null);
  const [agoraConfig,    setAgoraConfig]   = useState(null);
  const [roomBooking,    setRoomBooking]   = useState(null);
  const [profile,        setProfile]       = useState(null);      // puja personalisation
  const [showModal,      setShowModal]     = useState(false);     // personalisation modal
  const [showGuestGate,  setShowGuestGate] = useState(false);     // guest booking gate

  // Load saved profile on mount
  useEffect(() => {
    const saved = localStorage.getItem("veda_puja_profile");
    if (saved) {
      try { setProfile(JSON.parse(saved)); }
      catch (_) {}
    }
  }, []);

  const go   = s  => setStep(s);
  // fullCream: covers entire viewport width, hides fixed PujaBackground
  const fullCream = { background:CK.pageBg, width:"100%", minHeight:"100vh", position:"relative" };
  const wrap = { padding:"32px", maxWidth:"900px", margin:"0 auto", width:"100%" };

  // Flatten API catalog puja (object fields) into simple strings for booking steps
  const normalizePuja = useCallback((p, lang) => {
    const langCode = { hindi:"hi", telugu:"te", tamil:"ta", bengali:"bn", marathi:"mr" }[lang || profile?.language] || "en";
    const name   = (p.names  && (p.names[langCode]  || p.names.en))  || p.name  || "";
    const deity  = (p.deity  && (typeof p.deity === "object" ? (p.deity[langCode] || p.deity.en || "") : p.deity)) || "";
    const benefit = p.benefits?.[0] || p.benefit || "";
    const sanskrit = p.sanskrit_mantra || p.sanskrit || "";
    return { ...p, name, deity, benefit, sanskrit };
  }, [profile?.language]);

  // ── Full-screen video room ───────────────────────────────────────────────────
  if (step === "room" && agoraConfig && roomBooking) {
    return (
      <PujaRoom
        booking={roomBooking}
        agoraConfig={agoraConfig}
        onLeave={() => { setStep("browse"); setAgoraConfig(null); setRoomBooking(null); }}
      />
    );
  }

  const handleJoinRoom = (b, cfg) => { setRoomBooking(b); setAgoraConfig(cfg); go("room"); };

  const handleSaveProfile = (p) => {
    setProfile(p);
    setShowModal(false);
    // Also save to backend if logged in
    if (user?.id) {
      fetch(`${API}/puja/profile`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ user_id: user.id, state: p.state, tradition: p.tradition }),
      }).catch(() => {});
    }
  };

  // ── Role selector ────────────────────────────────────────────────────────────
  if (!role) {
    // Guests get the immersive sacred landing instead of plain role selector
    if (!user?.id) return <GuestPujaLanding onEnter={() => setRole("devotee")} />;
    return <RoleSelector onSelect={r => setRole(r)} />;
  }

  // ── Priest portal ────────────────────────────────────────────────────────────
  if (role === "priest") {
    return (
      <div style={fullCream}><div style={wrap}>
        <button onClick={() => setRole(null)} style={backBtnStyle}>← Back</button>
        <PriestPortalTab user={user} />
      </div></div>
    );
  }

  // ── Devotee: booking flow steps ──────────────────────────────────────────────
  if (step === "detail")  return (
    <div style={fullCream}>
      <div style={wrap}>
        {showGuestGate && (
          <GuestBookingGate
            puja={puja}
            onLogin={() => { setShowGuestGate(false); onLoginRequired?.(); }}
            onClose={() => setShowGuestGate(false)}
          />
        )}
        <PujaDetailStep
          puja={puja}
          profile={profile}
          onBack={() => go("browse")}
          onBook={() => {
            if (!user?.id) { setShowGuestGate(true); }
            else { go("type"); }
          }}
        />
      </div>
    </div>
  );
  if (step === "type")    return <div style={fullCream}><div style={wrap}><BookingTypeStep puja={puja} onBack={() => go("detail")} onSelect={t => { setBookingType(t); go("tier"); }} /></div></div>;
  if (step === "tier")    return <div style={fullCream}><div style={wrap}><TierStep puja={puja} bookingType={bookingType} onBack={() => go("type")} onSelect={t => { setTier(t); go("form"); }} /></div></div>;
  if (step === "form")    return <div style={fullCream}><div style={wrap}><BookingFormStep puja={puja} bookingType={bookingType} tier={tier} onBack={() => go("tier")} onConfirm={b => { setBooking(b); go("payment"); }} /></div></div>;
  if (step === "payment") {
    if (!user?.id) {
      // Gate: guest reached checkout — ask to sign in
      return (
        <div style={fullCream}><div style={{ ...wrap, textAlign: "center", padding: "80px 32px" }}>
          <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🪔</div>
          <div style={{ fontFamily: font.title, fontWeight: 700, fontSize: "1.35rem", color: CK.glow, marginBottom: "10px" }}>
            Almost there
          </div>
          <div style={{ color: CK.dim, fontSize: "1.05rem", lineHeight: 1.7, maxWidth: 400, margin: "0 auto 32px" }}>
            Create your free account to confirm booking. Your puja selection is saved — you won't need to pick again.
          </div>
          <button
            onClick={() => onLoginRequired?.()}
            style={{
              background: "var(--saffron)",
              border: "none", borderRadius: "10px", padding: "13px 36px",
              color: "white", fontFamily: font.title, fontWeight: 700,
              fontSize: "1rem", cursor: "pointer",
              boxShadow: "0 4px 20px rgba(232,131,42,0.3)", marginBottom: "16px",
              display: "block", margin: "0 auto 16px",
            }}
          >
            Create free account →
          </button>
          <button onClick={() => go("form")} style={{
            background: "transparent", border: `1px solid ${CK.border}`,
            borderRadius: "8px", padding: "9px 24px",
            color: CK.dim, fontFamily: font.body,
            fontSize: "0.98rem", cursor: "pointer",
          }}>
            ← Back to booking form
          </button>
        </div></div>
      );
    }
    return <div style={fullCream}><div style={wrap}><PaymentStep booking={{ ...booking, user }} onBack={() => go("form")} onSuccess={() => go("success")} /></div></div>;
  }
  if (step === "success") return <div style={fullCream}><div style={wrap}><SuccessStep booking={booking} onDone={() => { setStep("browse"); setPuja(null); setBookingType(null); setTier(null); setBooking(null); }} /></div></div>;

  // ── Devotee: browse / my bookings ────────────────────────────────────────────
  return (
    <div style={{ width:"100%", padding:"0", background:CK.pageBg, minHeight:"100vh", position:"relative" }}>
      {/* Personalisation modal — shown on first visit or when "Change" is clicked */}
      {(showModal || !profile) && (
        <PersonalisationModal onSave={handleSaveProfile} />
      )}

      <div style={{ padding:"16px 32px 0" }}>
        <button onClick={() => { setRole(null); setStep("browse"); }} style={backBtnStyle}>← Back</button>
        <TabBar active={view} onChange={v => setView(v)} />
      </div>
      {view === "browse" && (
        <BrowseStep
          profile={profile}
          onEditProfile={() => setShowModal(true)}
          onSelect={p => {
            // Normalize at selection time so all downstream steps get flat strings
            setPuja(normalizePuja(p));
            go("detail");
          }}
        />
      )}
      {view === "myBookings" && <MyBookingsTab user={user} onJoin={handleJoinRoom} />}
    </div>
  );
}

const backBtnStyle = {
  background:"transparent", border:"none",
  color:"var(--text-dim)", fontSize:"0.98rem",
  cursor:"pointer", padding:"0", marginBottom:"20px",
  display:"block", fontFamily:"'Inter',sans-serif",
};
